/**
 * app-js-2026.js
 *
 * Bagian 1 (IIFE): banner horizontal scroll, mega menu, nav sidebar
 * Bagian 2 (jQuery): cart sidebar, search sidebar, account sidebars
 *
 * API global: window.chHeaderModals — koordinasi tutup modal header
 */
(function () {
  'use strict';

  /* ——— Konstanta & state banner ——— */
  var ANIM_MS = 300;       /* durasi animasi tutup mega / sidebar (ms) */
  var DESKTOP_MIN = 990;   /* lebar min layar desktop (mega menu aktif) */
  var BANNER_SCROLL_TOP_MAX = 2; /* scroll halaman harus ~0 sebelum banner horizontal aktif */
  var BANNER_MOBILE_MAX = 1023; /* di bawah ini: swipe native, bukan wheel hijack */
  var sliders = [];        /* daftar instance .js-four-blocks-swiper */
  var wheelReady = false;  /* wheel listener sudah terpasang */
  var viewObserver = null; /* IntersectionObserver untuk slider banner */
  var domReady = false;    /* listener global sudah terpasang */

  /* ——— Utilitas umum ——— */

  /**
   * Jalankan callback setelah DOM siap.
   * @param {Function} fn
   */
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /**
   * Cek layar desktop (mega menu aktif).
   * @returns {boolean}
   */
  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  /**
   * Ada mega menu, cart, search, atau nav sidebar yang sedang buka.
   * @returns {boolean}
   */
  function isModalOpen() {
    if (document.body.classList.contains('ch-mega-scroll-lock')) return true;
    if (document.body.classList.contains('cart-scroll-lock')) return true;
    if (document.body.classList.contains('search-scroll-lock')) return true;
    if (document.querySelector('.custom-sidebar-menu.is-open')) return true;
    if (document.body.classList.contains('account-scroll-lock')) return true;
    if (document.body.classList.contains('superga-care-scroll-lock')) return true;
    if (document.body.classList.contains('overlay-material-scroll-lock')) return true;
    return false;
  }

  /**
   * Paksa sembunyikan overlay mega (hindari double backdrop dengan cart/nav).
   */
  function forceHideMegaOverlay() {
    var overlay = getMegaOverlay();
    var section = document.querySelector('.section-custom-header');
    if (overlay) {
      overlay.setAttribute('hidden', '');
      overlay.classList.remove('ch-mega-overlay--visible');
      overlay.setAttribute('aria-hidden', 'true');
    }
    if (section) section.classList.remove('ch-mega-is-open');
    document.body.classList.remove('ch-mega-scroll-lock');
    releaseBannerLockIfOk();
  }

  /**
   * Tutup panel modal lain; hanya satu yang boleh terbuka.
   * @param {'mega'|'nav'|'cart'|'search'|'account'} keep — panel yang dipertahankan
   */
  function closeOtherModals(keep) {
    var api = window.chHeaderModals;
    if (!api) return;
    if (keep !== 'mega') {
      if (api.closeMegasImmediately) {
        api.closeMegasImmediately();
      } else {
        if (api.closeMegas) api.closeMegas();
        forceHideMegaOverlay();
      }
    }
    if (keep !== 'nav' && api.closeNavSidebar) api.closeNavSidebar();
    if (keep !== 'cart' && api.closeCart) api.closeCart();
    if (keep !== 'search' && api.closeSearch) api.closeSearch();
    if (keep !== 'account' && api.closeAccountSidebars) api.closeAccountSidebars();
    if (keep !== 'overlay-material' && api.closeOverlayMaterial) api.closeOverlayMaterial();
    if (keep !== 'superga-care' && api.closeSupergaCare) api.closeSupergaCare();
  }

  /**
   * Batasi angka antara min dan max.
   * @param {number} n
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }

  /**
   * Tinggi header dari CSS var --header-height atau ukuran section.
   * @returns {number}
   */
  function getHeaderHeight() {
    var h = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    if (!isNaN(h) && h > 0) return h;
    var el = document.querySelector('.section-custom-header, .section-header');
    return el ? el.offsetHeight : 0;
  }

  /**
   * Posisi bawah header sticky di viewport (real-time, bukan cuma CSS var).
   * @returns {number}
   */
  function getHeaderBottom() {
    var section = document.querySelector('.section-custom-header, .section-header');
    if (!section) return getHeaderHeight();
    return Math.max(section.getBoundingClientRect().bottom, 0);
  }

  /**
   * Halaman sudah di paling atas (scrollY ≈ 0).
   * @returns {boolean}
   */
  function isPageScrollAtTop() {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    return scrollTop <= BANNER_SCROLL_TOP_MAX;
  }

  /**
   * Berapa px elemen terlihat di area bawah header.
   * @param {DOMRect} rect
   * @returns {number}
   */
  function visibleBelowHeader(rect) {
    var top = Math.max(rect.top, getHeaderBottom());
    var bottom = Math.min(rect.bottom, window.innerHeight);
    return Math.max(bottom - top, 0);
  }

  /**
   * Toggle class lock body saat scroll banner horizontal.
   * Tidak melepas lock jika ada modal lain terbuka.
   * @param {boolean} on
   */
  function setBannerLock(on) {
    if (!on && isModalOpen()) return;
    document.body.classList.toggle('four-blocks-scroll-lock', on);
  }

  /* ——— Banner horizontal scroll (four blocks) ——— */

  /** Cek apakah banner harus memakai scroll native touch/mobile. */
  function isBannerTouchScrollMode() {
    return false;
  }

  /**
   * Geser track banner horizontal sebesar delta px.
   * @param {{ pos: number, max: number, wrap: HTMLElement }} item
   * @param {number} delta
   * @returns {boolean} true jika posisi berubah
   */
  function setBannerTrackX(item, pos) {
    item.el.style.setProperty('--four-blocks-track-x', -pos + 'px');
  }

  /** Geser posisi slider banner lalu update transform track. */
  function moveSlider(item, delta) {
    var next = clamp(item.pos + delta, 0, item.max);
    if (next === item.pos) return false;
    item.pos = next;
    setBannerTrackX(item, item.pos);
    return true;
  }

  /**
   * Atur gap, padding kiri/kanan, dan hitung ulang posisi/max slide banner.
   * @param {{ el: HTMLElement, wrap: HTMLElement, slides: NodeList, pos: number, max: number }} item
   */
  function layoutSlider(item) {
    if (isBannerTouchScrollMode()) {
      item.el.classList.add('is-touch-scroll');
      item.el.style.removeProperty('--four-blocks-pad-left');
      item.el.style.removeProperty('--four-blocks-pad-right');
      item.el.style.setProperty('--four-blocks-track-x', '0px');
      item.pos = 0;
      item.max = 0;
      return;
    }

    item.el.classList.remove('is-touch-scroll');
    var slides = item.slides;
    var content = [];
    var i;

    for (i = 0; i < slides.length; i++) {
      if (!slides[i].classList.contains('slot-scroll')) content.push(slides[i]);
    }

    var first = content[0] || slides[0];
    var last = content[content.length - 1] || first;
    var w = item.el.clientWidth;
    var padL = Math.max((w - first.getBoundingClientRect().width) / 2, 0);
    var padR = Math.max((w - last.getBoundingClientRect().width) / 2, 0);

    item.el.style.setProperty('--four-blocks-pad-left', Math.round(padL) + 'px');
    item.el.style.setProperty('--four-blocks-pad-right', Math.round(padR) + 'px');

    var oldMax = item.max;
    var ratio = oldMax > 0 ? item.pos / oldMax : 0;
    item.max = Math.max(item.wrap.scrollWidth - item.el.clientWidth, 0);
    item.pos = clamp(ratio * item.max, 0, item.max);
    setBannerTrackX(item, item.pos);
  }

  /**
   * Slider banner yang paling terlihat di viewport (target event wheel).
   * @returns {object|null}
   */
  function getActiveSlider() {
    if (isBannerTouchScrollMode()) return null;
    if (!isPageScrollAtTop()) return null;

    var best = null;
    var bestVis = 0;
    var i;

    for (i = 0; i < sliders.length; i++) {
      var s = sliders[i];
      if (!s.el.isConnected || !s.visible) continue;

      var rect = s.el.getBoundingClientRect();
      var vis = visibleBelowHeader(rect);
      if (vis > 40 && vis > bestVis) {
        bestVis = vis;
        best = s;
      }
    }
    return best;
  }

  /**
   * Handler wheel: ubah scroll vertikal menjadi geser banner horizontal.
   * @param {WheelEvent} e
   */
  function onWheel(e) {
    if (isModalOpen()) return;
    if (isBannerTouchScrollMode()) return;

    if (!isPageScrollAtTop()) {
      setBannerLock(false);
      return;
    }

    var s = getActiveSlider();
    if (!s) {
      setBannerLock(false);
      return;
    }

    var y = e.clientY;
    var box = s.el.getBoundingClientRect();
    var headerBottom = getHeaderBottom();

    if (y < headerBottom || y < box.top || y > box.bottom) {
      setBannerLock(false);
      return;
    }

    var delta = e.deltaY;
    if (!delta) return;

    if (delta > 0 && s.pos >= s.max - 0.5) {
      setBannerLock(false);
      return;
    }
    if (delta < 0 && s.pos <= 0.5) {
      setBannerLock(false);
      return;
    }

    e.preventDefault();
    setBannerLock(true);
    moveSlider(s, delta);
  }

  /** Lepas banner lock kalau user scroll halaman turun dari paling atas. */
  function onBannerPageScroll() {
    if (!isPageScrollAtTop()) {
      setBannerLock(false);
    }
  }

  var touchStartY = 0;
  var touchStartX = 0;

  function onTouchStart(e) {
    if (e.touches.length !== 1) return;
    touchStartY = e.touches[0].clientY;
    touchStartX = e.touches[0].clientX;
  }

  function onTouchMove(e) {
    if (isModalOpen()) return;
    if (e.touches.length !== 1) return;

    if (!isPageScrollAtTop()) {
      setBannerLock(false);
      return;
    }

    var s = getActiveSlider();
    if (!s) {
      setBannerLock(false);
      return;
    }

    var y = e.touches[0].clientY;
    var x = e.touches[0].clientX;
    var box = s.el.getBoundingClientRect();
    var headerBottom = getHeaderBottom();

    if (touchStartY < headerBottom || touchStartY < box.top || touchStartY > box.bottom) {
      setBannerLock(false);
      return;
    }

    var deltaY = touchStartY - y;
    if (!deltaY) return;

    if (deltaY > 0 && s.pos >= s.max - 0.5) {
      setBannerLock(false);
      return;
    }
    if (deltaY < 0 && s.pos <= 0.5) {
      setBannerLock(false);
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
    }
    setBannerLock(true);
    moveSlider(s, deltaY);
    touchStartY = y;
  }

  /** Pasang listener wheel + resize (sekali) */
  function bindWheel() {
    if (wheelReady) return;
    wheelReady = true;
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('scroll', onBannerPageScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
  }

  /** Hitung ulang layout semua slider banner saat resize window. */
  function onResize() {
    var i;
    for (i = 0; i < sliders.length; i++) {
      if (sliders[i].el.isConnected) layoutSlider(sliders[i]);
    }
  }

  /**
   * Ambil/buat IntersectionObserver untuk pantau slider masuk/keluar viewport.
   * @returns {IntersectionObserver}
   */
  function getViewObserver() {
    if (viewObserver) return viewObserver;

    /** Callback observer: update flag visible tiap slider */
    viewObserver = new IntersectionObserver(function (entries) {
      var i;
      for (i = 0; i < entries.length; i++) {
        var j;
        for (j = 0; j < sliders.length; j++) {
          if (sliders[j].el === entries[i].target) {
            sliders[j].visible = entries[i].isIntersecting;
          }
        }
      }
      if (!getActiveSlider()) setBannerLock(false);
    }, { threshold: [0, 0.01, 0.5, 1] });

    return viewObserver;
  }

  /**
   * Inisialisasi semua .js-four-blocks-swiper di dalam root.
   * @param {Document|HTMLElement} root
   */
  function initBanner(root) {
    var list = root.querySelectorAll('.js-four-blocks-swiper:not([data-four-blocks-init])');
    var obs = getViewObserver();
    var n;

    for (n = 0; n < list.length; n++) {
      var el = list[n];
      var wrap = el.querySelector('.swiper-wrapper');
      var slideEls = el.querySelectorAll('.swiper-slide');
      if (!wrap || !slideEls.length) continue;

      var item = {
        el: el,
        wrap: wrap,
        slides: slideEls,
        pos: 0,
        max: 0,
        visible: false,
      };

      layoutSlider(item);
      sliders.push(item);
      el.setAttribute('data-four-blocks-init', 'true');
      obs.observe(el);
    }

    bindWheel();
  }

  /** Hapus slider saat section unload */
  function removeBanner(el) {
    var i;
    for (i = sliders.length - 1; i >= 0; i--) {
      if (sliders[i].el === el) {
        if (viewObserver) viewObserver.unobserve(el);
        el.removeAttribute('data-four-blocks-init');
        sliders[i].el.style.removeProperty('--four-blocks-track-x');
        sliders[i].el.style.removeProperty('--four-blocks-pad-left');
        sliders[i].el.style.removeProperty('--four-blocks-pad-right');
        sliders.splice(i, 1);
      }
    }
  }

  /* ——— Mega menu ——— */

  /**
   * Elemen overlay gelap di belakang mega menu.
   * @returns {HTMLElement|null}
   */
  function getMegaOverlay() {
    return document.getElementById('ch-mega-overlay');
  }

  /** Semua details mega yang sedang [open] */
  function getOpenMegas() {
    return document.querySelectorAll('header-menu .mega-menu[open]');
  }

  /**
   * Sinkronkan overlay mega, class section header, dan body scroll lock.
   */
  function syncMega() {
    var overlay = getMegaOverlay();
    var open = getOpenMegas().length > 0 && isDesktop();
    var section = document.querySelector('.section-custom-header');

    if (open) closeOtherModals('mega');

    if (overlay) {
      if (open) {
        overlay.removeAttribute('hidden');
        overlay.classList.add('ch-mega-overlay--visible');
        overlay.setAttribute('aria-hidden', 'false');
      } else {
        overlay.setAttribute('hidden', '');
        overlay.classList.remove('ch-mega-overlay--visible');
        overlay.setAttribute('aria-hidden', 'true');
      }
    }

    if (section) {
      section.classList.toggle('ch-mega-is-open', open);
    }

    document.body.classList.toggle('ch-mega-scroll-lock', open);
    document.body.classList.toggle('four-blocks-scroll-lock', open);
  }

  /**
   * Sembunyikan overlay mega saja (panel masih dalam animasi tutup).
   */
  function hideMegaOverlayOnly() {
    var overlay = getMegaOverlay();
    var section = document.querySelector('.section-custom-header');
    if (overlay) {
      overlay.setAttribute('hidden', '');
      overlay.classList.remove('ch-mega-overlay--visible');
      overlay.setAttribute('aria-hidden', 'true');
    }
    if (section) section.classList.remove('ch-mega-is-open');
  }

  /**
   * Selesaikan proses tutup satu panel mega (hapus open, sync UI).
   * @param {HTMLDetailsElement} details
   */
  function finishCloseMega(details) {
    details.classList.remove('mega-menu--closing');
    details.removeAttribute('open');
    var sum = details.querySelector('summary');
    if (sum) sum.setAttribute('aria-expanded', 'false');
    syncMega();
  }

  /**
   * Tutup satu panel mega menu (dengan animasi di desktop).
   * @param {HTMLDetailsElement} details
   */
  function closeMega(details) {
    if (!details || !details.hasAttribute('open')) return;
    if (details.classList.contains('mega-menu--closing')) return;

    if (!isDesktop()) {
      finishCloseMega(details);
      return;
    }

    details.classList.add('mega-menu--closing');
    hideMegaOverlayOnly();
    setTimeout(function () {
      finishCloseMega(details);
    }, ANIM_MS);
  }

  /** Tutup semua panel mega menu yang sedang terbuka. */
  function closeAllMegas() {
    var list = getOpenMegas();
    var i;
    for (i = 0; i < list.length; i++) {
      closeMega(list[i]);
    }
  }

  /**
   * Tutup semua mega menu seketika (tanpa animasi) — dipakai saat buka cart/search/nav.
   */
  function closeAllMegasImmediately() {
    var list = getOpenMegas();
    var i;
    for (i = 0; i < list.length; i++) {
      var details = list[i];
      details.classList.remove('mega-menu--closing');
      finishCloseMega(details);
    }
    forceHideMegaOverlay();
  }

  /**
   * Patch komponen header-menu Dawn + pasang event toggle mega.
   * @param {HTMLElement} headerMenu
   */
  function setupHeaderMenu(headerMenu) {
    if (headerMenu.dataset.chMegaReady) return;

    var details = headerMenu.querySelector('.mega-menu');
    if (!details) return;

    /** Dawn: update posisi header & preventHide saat mega buka */
    headerMenu.onToggle = function () {
      var header = document.querySelector('.header-wrapper');
      if (!header) return;
      header.preventHide = this.mainDetailsToggle.open;
      if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop')) return;
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        Math.floor(header.getBoundingClientRect().bottom) + 'px'
      );
    };

    /** Dawn: method close — tutup mega dari komponen header-menu */
    headerMenu.close = function () {
      closeMega(this.querySelector('.mega-menu'));
    };

    var summary = details.querySelector('summary');
    if (summary) {
      /** Klik summary saat sudah open → tutup (bukan toggle bawaan) */
      summary.addEventListener('click', function (e) {
        if (!details.hasAttribute('open')) return;
        if (details.classList.contains('mega-menu--closing')) return;
        e.preventDefault();
        closeMega(details);
      });
    }

    /** Event native details toggle — sync overlay & tutup mega lain */
    details.addEventListener('toggle', function () {
      if (!details.open) {
        if (!details.classList.contains('mega-menu--closing')) syncMega();
        return;
      }

      var others = getOpenMegas();
      var i;
      for (i = 0; i < others.length; i++) {
        if (others[i] !== details) closeMega(others[i]);
      }
      syncMega();
    });

    headerMenu.dataset.chMegaReady = 'true';
  }

  /**
   * Inisialisasi mega menu + klik overlay di dalam root.
   * @param {Document|HTMLElement} root
   */
  function initMega(root) {
    var menus = root.querySelectorAll('header-menu');
    var i;

    for (i = 0; i < menus.length; i++) {
      setupHeaderMenu(menus[i]);
    }

    var overlay = getMegaOverlay();
    if (overlay && !overlay.dataset.chMegaReady) {
      overlay.addEventListener('click', closeAllMegas);
      overlay.dataset.chMegaReady = 'true';
    }

    syncMega();
  }

  /** Lepas class lock banner jika mega menu tidak sedang buka. */
  function releaseBannerLockIfOk() {
    if (!document.body.classList.contains('ch-mega-scroll-lock')) {
      document.body.classList.remove('four-blocks-scroll-lock');
    }
  }

  /* ——— Nav sidebar (hamburger) ——— */

  /**
   * Panel nav sidebar mobile.
   * @returns {HTMLElement|null}
   */
  function getSidebarPanel() {
    return document.querySelector('.section-sidebar-menu .custom-sidebar-menu');
  }

  /** Clear semua field input di popup/sidebar native saat ditutup. */
  function clearNativePopupInputs(root) {
    if (!root) return;

    root.querySelectorAll('input, textarea, select').forEach(function (field) {
      var type = (field.getAttribute('type') || '').toLowerCase();

      if (type === 'hidden' || type === 'button' || type === 'submit' || type === 'reset') return;

      if (type === 'checkbox' || type === 'radio') {
        field.checked = false;
        return;
      }

      if (field.tagName === 'SELECT') {
        field.selectedIndex = 0;
        return;
      }

      field.value = '';
    });
  }

  /**
   * Tutup nav sidebar + animasi + reset aria-expanded.
   * @param {HTMLElement} menu
   */
  function closeSidebar(menu) {
    if (!menu || !menu.classList.contains('is-open')) return;
    if (menu.dataset.closing === 'true') return;

    menu.dataset.closing = 'true';
    clearNativePopupInputs(menu);
    menu.classList.remove('is-open');

    setTimeout(function () {
      document.body.classList.remove('overflow-hidden');
      releaseBannerLockIfOk();
      menu.dataset.closing = 'false';
      var toggles = document.querySelectorAll('.custom-sidebar-menu__toggle');
      var i;
      for (i = 0; i < toggles.length; i++) {
        toggles[i].setAttribute('aria-expanded', 'false');
      }
    }, ANIM_MS);
  }

  /** Tutup panel nav sidebar (dipanggil dari chHeaderModals). */
  function closeNavSidebar() {
    closeSidebar(getSidebarPanel());
  }

  /**
   * Buka nav sidebar + lock scroll body.
   * @param {HTMLElement} menu
   */
  function openSidebar(menu) {
    if (!menu || menu.dataset.closing === 'true') return;
    closeOtherModals('nav');
    menu.classList.add('is-open');
    document.body.classList.add('overflow-hidden', 'four-blocks-scroll-lock');
    var toggles = document.querySelectorAll('.custom-sidebar-menu__toggle');
    var i;
    for (i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Pasang event close & overlay pada satu panel sidebar.
   * @param {HTMLElement} menu
   */
  function bindSidebarPanel(menu) {
    if (!menu || menu.dataset.chSidebarReady) return;

    var closeBtns = menu.querySelectorAll('.custom-sidebar-menu__close');
    var overlay = menu.querySelector('.modal-overlay');
    var j;

    for (j = 0; j < closeBtns.length; j++) {
      closeBtns[j].addEventListener('click', function () {
        closeSidebar(menu);
      });
    }
    if (overlay) {
      overlay.addEventListener('click', function () {
        closeSidebar(menu);
      });
    }

    menu.dataset.chSidebarReady = 'true';
  }

  /**
   * Pasang event klik pada tombol hamburger.
   * @param {HTMLElement} btn
   */
  function bindSidebarToggle(btn) {
    if (!btn || btn.dataset.chToggleBound) return;

    btn.addEventListener('click', function () {
      var panel = getSidebarPanel();
      if (panel) openSidebar(panel);
    });

    btn.dataset.chToggleBound = 'true';
  }

  /**
   * Inisialisasi nav sidebar di dalam root.
   * @param {Document|HTMLElement} root
   */
  function initSidebar(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var panels = scope.querySelectorAll('.section-sidebar-menu .custom-sidebar-menu');
    var toggles = scope.querySelectorAll('.custom-sidebar-menu__toggle');
    var i;

    for (i = 0; i < panels.length; i++) {
      bindSidebarPanel(panels[i]);
    }
    for (i = 0; i < toggles.length; i++) {
      bindSidebarToggle(toggles[i]);
    }

    var newsletterResult = scope.querySelector('[data-sidebar-newsletter-result]');
    var newsletterPanel = newsletterResult ? newsletterResult.closest('.custom-sidebar-menu') : null;
    if (newsletterPanel) {
      requestAnimationFrame(function () {
        openSidebar(newsletterPanel);
        var message = newsletterPanel.querySelector('.cf__newsletter-message');
        if (message && message.focus) message.focus();
      });
    }
  }

  /* ——— Init & event global ——— */

  /**
   * Inisialisasi semua modul header di root (document atau section Theme Editor).
   * @param {Document|HTMLElement} root
   */
  function init(root) {
    initBanner(root);
    initMega(root);
    initSidebar(root);
  }

  /**
   * Shopify Theme Editor: section baru dimuat.
   * @param {CustomEvent} e
   */
  function onSectionLoad(e) {
    init(e.target);
  }

  /**
   * Shopify Theme Editor: section di-unload — bersihkan slider banner.
   * @param {CustomEvent} e
   */
  function onSectionUnload(e) {
    var removed = e.target.querySelectorAll('.js-four-blocks-swiper');
    var i;
    for (i = 0; i < removed.length; i++) {
      removeBanner(removed[i]);
    }
    if (!getActiveSlider()) setBannerLock(false);
  }

  /**
   * Handler Escape: tutup modal berurutan mega → nav → search → cart.
   * @param {KeyboardEvent} e
   */
  function onKeydown(e) {
    if (e.key !== 'Escape') return;

    if (getOpenMegas().length) {
      closeAllMegas();
      return;
    }

    var sidebar = getSidebarPanel();
    if (sidebar && sidebar.classList.contains('is-open')) {
      closeSidebar(sidebar);
      return;
    }

    var api = window.chHeaderModals;
    if (api && api.isSearchOpen && api.isSearchOpen() && api.closeSearch) {
      api.closeSearch();
      return;
    }

    if (api && api.isCartOpen && api.isCartOpen() && api.closeCart) {
      api.closeCart();
    }
  }

  /** Handler breakpoint desktop ↔ mobile: sync ulang overlay mega. */
  function onDesktopChange() {
    syncMega();
  }

  /** Pasang semua listener global sekali (section load, keydown, breakpoint). */
  function bindDom() {
    if (domReady) return;
    domReady = true;

    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener('keydown', onKeydown);
    window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px)').addEventListener('change', onDesktopChange);
  }

  /** API global — diperluas cart & search di bagian jQuery bawah */
  window.chHeaderModals = {
    closeMegas: closeAllMegas,
    closeMegasImmediately: closeAllMegasImmediately,
    closeNavSidebar: closeNavSidebar,
    closeOthers: closeOtherModals,
  };

  /** Entry point bagian 1: pasang listener + init saat DOM ready */
  function start() {
    bindDom();
    onReady(function () {
      init(document);
    });
  }

  start();
})();

/* =============================================================================
 * BAGIAN 2: Cart sidebar & search sidebar (jQuery)
 * ============================================================================= */
jQuery(function ($) {
  window.chHeaderModals = window.chHeaderModals || {};

  /** Clear input popup/sidebar tanpa menghapus hidden system fields seperti return_to. */
  function clearPopupInputs($root, options) {
    options = options || {};
    if (!$root || !$root.length) return;

    $root.find('input, textarea, select').each(function () {
      var $field = $(this);
      var type = String($field.attr('type') || '').toLowerCase();

      if (!options.includeHidden && type === 'hidden') return;
      if (type === 'button' || type === 'submit' || type === 'reset') return;

      if (type === 'checkbox' || type === 'radio') {
        $field.prop('checked', false).removeAttr('checked');
        return;
      }

      if (this.tagName === 'SELECT') {
        this.selectedIndex = 0;
        return;
      }

      $field.val('');
    });
  }

  /* ——— Cart sidebar ——— */
  var $cartList = $('#cart-product-list');
  var hasCartSidebar = $('#cart-sidebar').length > 0;

  /**
   * Tutup cart sidebar + lepas scroll lock body.
   */
  function closeCartSidebar() {
    $('.cart-sidebar-root').removeClass('is-open');
    $('.cart-sidebar').removeClass('show-sidebar');
    $('html, body').removeClass('cart-scroll-lock');
    $('#cart-trigger, .header__icon--cart').attr('aria-expanded', 'false');
  }

  /**
   * Cek apakah cart sidebar sedang terbuka.
   * @returns {boolean}
   */
  function isCartOpen() {
    return $('.cart-sidebar').hasClass('show-sidebar');
  }

  /* Daftarkan cart ke API global chHeaderModals */
  window.chHeaderModals.closeCart = closeCartSidebar;
  window.chHeaderModals.isCartOpen = isCartOpen;

  var CART_PRICE_CURRENCY = 'IDR';

  /**
   * Format angka harga untuk tampilan (tanpa label mata uang).
   * @param {number|string} cents
   * @returns {string}
   */
  function formatPriceAmount(cents) {
    var n = parseInt(cents, 10);
    if (isNaN(n)) return '';
    return n.toLocaleString('id-ID');
  }

  /**
   * HTML satu baris harga: span currency + span amount.
   * @param {string} priceClass — class tambahan (price-sale / price-regular)
   * @param {number|string} cents
   * @returns {string}
   */
  function buildPriceRowHtml(priceClass, cents) {
    var amount = formatPriceAmount(cents);
    if (!amount) return '';

    return [
      '<div class="price ' + priceClass + '">',
      '  <span class="price-currency">' + CART_PRICE_CURRENCY + '</span>',
      '  <span class="price-amount">' + amount + '</span>',
      '</div>',
    ].join('\n');
  }

  /**
   * Konversi nilai cents Shopify ke string tampilan (potong 2 digit terakhir).
   * @param {number} amount
   * @returns {string}
   */
  function centsToDisplay(amount) {
    var s = String(amount);
    return s.length > 2 ? s.substring(0, s.length - 2) : s;
  }

  /** Inisialisasi window.productCache (Map) jika belum ada. */
  function ensureProductCache() {
    if (!(window.productCache instanceof Map)) {
      window.productCache = new Map();
    }
  }

  /**
   * Ambil data produk dari /products/{handle}.js (dengan cache Map).
   * @param {string} handle
   * @returns {Promise<object>}
   */
  async function getProductData(handle) {
    ensureProductCache();

    if (window.productCache.has(handle)) {
      return window.productCache.get(handle);
    }

    var res = await fetch('/products/' + handle + '.js');
    if (!res.ok) throw new Error('Gagal ambil product ' + handle);
    var product = await res.json();
    window.productCache.set(handle, product);
    return product;
  }

  /**
   * Ambil variant pertama yang bisa dipakai untuk icon cart/wishlist.
   * @param {object} product
   * @returns {object}
   */
  function getWishlistFirstVariant(product) {
    // Ambil array variant dari product, fallback ke array kosong kalau data tidak ada.
    var variants = product && product.variants ? product.variants : [];
    var i;

    // Prioritaskan variant pertama yang masih available untuk tombol cart.
    for (i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }

    // Kalau semua sold out / tidak ada flag available, pakai variant pertama supaya data-variant tetap ada.
    return variants[0] || {};
  }

  /**
   * Buat list size sederhana dari variants product wishlist.
   * @param {object} product
   * @returns {string}
   */
  function buildWishlistSizeMarkup(product) {
    // Ambil semua variant dari product hasil /products/{handle}.js.
    var variants = product && product.variants ? product.variants : [];
    var sizeOptionIndex = 0;
    var sizes = [];
    var availableSizes = [];

    // Cari index option size supaya urutannya sama dengan product-card-2026.
    (product.options || []).forEach(function (option, index) {
      if (String(option).toLowerCase().indexOf('size') !== -1) {
        sizeOptionIndex = index;
      }
    });

    // Kumpulkan size unik dari option variant.
    variants.forEach(function (variant) {
      var size = variant.options ? variant.options[sizeOptionIndex] : '';

      // Skip "Default Title" karena bukan size asli.
      if (size && size !== 'Default Title' && sizes.indexOf(size) === -1) {
        sizes.push(size);
      }

      // Simpan size available supaya class unavailable bisa sama dengan product-card-2026.
      if (size && variant.available && availableSizes.indexOf(size) === -1) {
        availableSizes.push(size);
      }
    });

    // Kalau tidak ada size, tampilkan fallback sama seperti snippet product-card-2026.
    if (!sizes.length) {
      return '<span class="product-card-2026__size-empty">Out of stock</span>';
    }

    // Ubah list size jadi markup product-card-2026 supaya CSS-nya ikut.
    return [
      '<div class="product-card-2026__size-list">',
      sizes.map(function (size) {
        var unavailable = availableSizes.indexOf(size) === -1 ? ' is-unavailable' : '';
        return '<span class="product-card-2026__size-item' + unavailable + '">' + escapeHtml(size) + '</span>';
      }).join(''),
      '</div>',
    ].join('');
  }

  /**
   * Format harga wishlist sama seperti product-card-2026.
   * @param {number|string} cents
   * @returns {string}
   */
  function formatWishlistProductCardPrice(cents) {
    var amount = Math.round((parseInt(cents, 10) || 0) / 100);

    return CART_PRICE_CURRENCY + ' ' + amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /**
   * HTML card fallback untuk wishlist saat ProductListRender bawaan tidak tersedia.
   * @param {object} product
   * @returns {string}
   */
  function buildWishlistProductCardHtml(product) {
    // Ambil variant utama untuk data cart dan wishlist icon.
    var variant = getWishlistFirstVariant(product);
    // Ambil image utama dari response product Shopify.
    var primaryImage = product.featured_image || (product.images && product.images[0]) || '';
    // Ambil image kedua untuk hover state seperti product-card-2026.
    var secondaryImage = product.images && product.images[1] ? product.images[1] : '';
    // Pakai URL dari response, fallback ke handle kalau URL tidak ada.
    var url = product.url || ('/products/' + product.handle);
    // Harga mengikuti product-card-2026: cukup tampilkan price utama.
    var price = product.price || variant.price || 0;

    // Markup ini mengikuti snippets/product-card-2026.liquid supaya CSS image/title/price sama.
    return [
      '<div class="related-products-item">',
      '  <article class="product-card-2026 wishlist-product-card-2026" data-initial-handle="' + escapeHtml(product.handle || '') + '">',
      '    <a class="product-card-2026__image-link" href="' + escapeHtml(url) + '">',
      primaryImage ? '      <img class="product-card-2026__image product-card-2026__image--primary" src="' + escapeHtml(primaryImage) + '" alt="' + escapeHtml(product.title || '') + '" loading="lazy">' : '',
      secondaryImage ? '      <img class="product-card-2026__image product-card-2026__image--secondary" src="' + escapeHtml(secondaryImage) + '" alt="' + escapeHtml(product.title || '') + '" loading="lazy">' : '',
      '    </a>',
      '    <div class="product-card-2026__variant-thumbs color-swatch-swiper swiper" aria-hidden="true"></div>',
      '    <div class="product-card-2026__info product-card-2026__info--default desktop-only">',
      '      <a class="product-card-2026__title" href="' + escapeHtml(url) + '">' + escapeHtml(product.title || '') + '</a>',
      '      <p class="product-card-2026__price">' + escapeHtml(formatWishlistProductCardPrice(price)) + '</p>',
      '    </div>',
      '    <div class="product-card-2026__info product-card-2026__info--default mobile-only">',
      '      <a class="product-card-2026__title" href="' + escapeHtml(url) + '">' + escapeHtml(product.title || '') + '</a>',
      '      <div class="color-swatches product-card-2026__mobile-color-count mobile-only"></div>',
      '      <p class="product-card-2026__price">' + escapeHtml(formatWishlistProductCardPrice(price)) + '</p>',
      '    </div>',
      '    <div class="product-card-2026__info product-card-2026__info--sizes" aria-hidden="true">' + buildWishlistSizeMarkup(product) + '</div>',
      '    <div class="wishlist-product-card-2026__icons">',
      '      <div class="smartwishlist" data-product="' + escapeHtml(product.id || '') + '" data-variant="' + escapeHtml(variant.id || '') + '"></div>',
      '      <div class="card__bag-icon card__add-to-cart" data-product="' + escapeHtml(product.id || '') + '" data-variant="' + escapeHtml(variant.id || '') + '"></div>',
      '    </div>',
      '  </article>',
      '</div>',
    ].join('\n');
  }

  /** Siapkan renderer wishlist kalau renderer bawaan tidak ada di theme. */
  function ensureWishlistProductRenderer() {
    // Pastikan namespace Shopify ada sebelum menambahkan renderer fallback.
    window.Shopify = window.Shopify || {};
    Shopify.ProductListRender = Shopify.ProductListRender || {};

    // Kalau renderer bawaan sudah ada dari app/theme, jangan override.
    if ($.isFunction(Shopify.ProductListRender.render)) return;

    // Buat renderer fallback supaya call Shopify.ProductListRender.render tetap bisa dipakai.
    Shopify.ProductListRender.render = function (options) {
      // Handles berasal dari app wishlist yang dibaca di renderWishlist().
      var handles = options.handles || [];
      // Wrapper target mengikuti id dari wishlist-section.liquid.
      var $wrapper = $('#' + options.wrapperId);

      // Ambil semua product by handle, lalu render card ke wrapper.
      Promise.all(handles.slice(0, options.howManyToShow || handles.length).map(getProductData))
        .then(function (products) {
          // Convert setiap product jadi HTML card.
          $wrapper.html(products.map(buildWishlistProductCardHtml).join(''));

          // Jalankan callback lama supaya sync cart/wishlist icon tetap hidup.
          if ($.isFunction(options.onComplete)) {
            options.onComplete(products);
          }
        })
        .catch(function (err) {
          // Lempar error ke handler lama supaya tetap muncul di console.
          if ($.isFunction(options.onError)) {
            options.onError(err);
          }
        });
    };
  }

  /** Ambil cart dari /cart.js */
  async function getCart() {
    var res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Gagal ambil cart');
    return res.json();
  }

  /**
   * Cari variant di object produk by ID.
   * @param {object} product
   * @param {number|string} variantId
   * @returns {object|null}
   */
  function variantFromProduct(product, variantId) {
    if (!product || !product.variants) return null;
    var id = Number(variantId);
    var i;
    for (i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === id) return product.variants[i];
    }
    return null;
  }

  /**
   * Enrich cart items dengan tags, compare_at_price, inventory per line.
   * @returns {Promise<object>}
   */
  async function getCartItemsWithTags() {
    var cart = await getCart();
    var itemsWithTags = await Promise.all(
      cart.items.map(async function (item) {
        var product = await getProductData(item.handle);
        var variant = variantFromProduct(product, item.variant_id);
        var tags = product.tags;
        var inventory =
          window.inventoryData && window.inventoryData[item.variant_id] != null
            ? window.inventoryData[item.variant_id]
            : variant
              ? variant.inventory_quantity
              : 0;

        return Object.assign({}, item, {
          compare_at_price: variant ? variant.compare_at_price : product.compare_at_price,
          inventory_quantity: inventory,
          tags: Array.isArray(tags) ? tags : tags ? tags.split(',').map(function (t) {
            return t.trim();
          }) : [],
        });
      })
    );

    return Object.assign({}, cart, { items: itemsWithTags });
  }

  /**
   * Tandai tombol wishlist (.card__add-to-cart) yang variant-nya sudah di cart.
   * @param {object} cart
   */
  function syncWishlistBagIcons(cart) {
    if (!$('#wishlist-section-products').length) return;

    var variantIds = {};
    if (cart && cart.items && cart.items.length) {
      cart.items.forEach(function (item) {
        variantIds[String(item.variant_id)] = true;
      });
    }

    $('#wishlist-section-products .card__add-to-cart').each(function () {
      var variantId = String($(this).attr('data-variant') || '');
      $(this).toggleClass('is-in-cart', !!variantIds[variantId]);
    });
  }

  /**
   * Update angka jumlah item di sidebar cart & icon header.
   * @param {number} count
   */
  function updateCartCounts(count) {
    var hasItems = count > 0;

    $('#cart-sidebar-count').text(count);
    $('.header__cart-desktop .bags-count-text').text('[' + count + ']');
    $('.header__cart-mobile-count').text(count).toggleClass('header__cart-mobile-count--empty', !hasItems);
    $('.data-cart-count').text(count);
  }

  /** Tampilkan panel isi / kosong cart */
  function toggleCartPanels(hasItems) {
    var $filled = $('#cart-sidebar-filled');
    var $empty = $('#cart-sidebar-empty');

    if (hasItems) {
      $empty.attr('hidden', 'hidden');
      $filled.removeAttr('hidden');
    } else {
      $filled.attr('hidden', 'hidden');
      $empty.removeAttr('hidden');
    }
  }

  /* SVG icon tombol qty cart */
  var cartIconMinus =
    '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12.5 6.5H0.5" stroke="black" stroke-linecap="square" stroke-linejoin="round"/>' +
    '</svg>';

  var cartIconClose =
    '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M0.707118 0.707112L9.1924 9.19239M9.1924 0.707112L0.707118 9.19239" stroke="black" stroke-linecap="square" stroke-linejoin="round"/>' +
    '</svg>';

  var cartIconPlus =
    '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M6.5 0.5V12.5M12.5 6.5H0.5" stroke="black" stroke-linecap="square" stroke-linejoin="round"/>' +
    '</svg>';

  /**
   * Escape string untuk aman di HTML attribute/content.
   * @param {*} text
   * @returns {string}
   */
  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Ambil nilai opsi Color & Size dari line item cart.
   * @param {object} item
   * @returns {{ color: string, size: string }}
   */
  function getCartVariantOptions(item) {
    var color = '';
    var size = '';
    var options = item.options_with_values || [];
    var i;

    for (i = 0; i < options.length; i++) {
      var name = options[i].name.toLowerCase();
      if (name === 'color') color = options[i].value;
      if (name === 'size') size = options[i].value;
    }

    return { color: color, size: size };
  }

  /**
   * HTML blok harga satu line item (sale + regular jika diskon).
   * @param {object} item
   * @returns {string}
   */
  function buildCartPriceHtml(item) {
    var linePrice = centsToDisplay(item.final_line_price);
    var isSale = item.original_line_price > item.final_line_price;
    var comparePrice = centsToDisplay(
      isSale ? item.original_line_price : item.compare_at_price * item.quantity
    );

    if (isSale) {
      return [
        '<div class="cart-product-price">',
        buildPriceRowHtml('price-sale', comparePrice),
        buildPriceRowHtml('price-regular', linePrice),
        '</div>',
      ].join('\n');
    }

    return [
      '<div class="cart-product-price">',
      buildPriceRowHtml('price-regular', linePrice),
      '</div>',
    ].join('\n');
  }

  /**
   * HTML satu baris produk lengkap di cart sidebar.
   * @param {object} item
   * @returns {string}
   */
  function buildCartItemHtml(item) {
    var variant = getCartVariantOptions(item);
    var priceHtml = buildCartPriceHtml(item);
    var minusClass = item.quantity === 1 ? ' change-item-minus-qty-1' : '';
    var plusDisabled = item.quantity >= item.inventory_quantity ? ' disabled' : '';
    var title = escapeHtml(item.product_title);
    var alt = escapeHtml(item.title);

    return [
      '<div class="cart-product-item">',
      '  <div class="cart-product-image">',
      '    <img src="' + item.image + '" alt="' + alt + '">',
      '  </div>',
      '  <div class="cart-product-content">',
      '    <div class="cart-product-content-header">',
      '     <div class="cart-product-title">' + title + '</div>',
      '     <div class="cart-product-variant">',
      '       <div class="cart-product-variant-item cart-product-variant-color">' + escapeHtml(variant.color) + '</div>',
      '       <div class="cart-product-variant-item cart-product-variant-size">' + escapeHtml(variant.size) + '</div>',
      '     </div>',
      '    </div>',
      '    <div class="cart-product-operation-price">',
      '      <div class="cart-product-operation">',
      '        <button',
      '          type="button"',
      '          class="change-item change-item-minus' + minusClass + '"',
      '          data-variant-id="' + item.variant_id + '"',
      '          data-quantity="' + item.quantity + '"',
      '        >',
      '          <div class="icon-minus">' + cartIconMinus + '</div>',
      '          <div class="icon-close">' + cartIconClose + '</div>',
      '        </button>',
      '        <div class="cart-product-qty">' + item.quantity + '</div>',
      '        <button',
      '          type="button"',
      '          class="change-item change-item-plus"' + plusDisabled,
      '          data-variant-id="' + item.variant_id + '"',
      '          data-quantity="' + item.quantity + '"',
      '        >',
      '          ' + cartIconPlus,
      '        </button>',
      '      </div>',
      '      <div class="cart-product-operation-price-item">',
      priceHtml,
      '      </div>',
      '    </div>',
      '  </div>',
      '</div>',
    ].join('\n');
  }

  /** Render semua item ke #cart-product-list */
  function renderCartItems(cart) {
    var html = '';
    var i;

    for (i = 0; i < cart.items.length; i++) {
      html += buildCartItemHtml(cart.items[i]);
      if (i < cart.items.length - 1) html += '\n';
    }

    $cartList.html(html);
  }

  /**
   * Fetch cart + refresh UI: count, list item, panel kosong/isi, wishlist icons.
   * Diekspos global sebagai window.syncCartUI.
   */
  function syncCartUI() {
    if (!hasCartSidebar) return Promise.resolve(null);

    return getCartItemsWithTags()
      .then(function (cart) {
        updateCartCounts(cart.item_count);

        if (cart.item_count > 0) {
          toggleCartPanels(true);
          renderCartItems(cart);
        } else {
          toggleCartPanels(false);
          $cartList.html('');
        }

        syncWishlistBagIcons(cart);
      })
      .catch(function (err) {
        console.error('[cart]', err);
        return null;
      });
  }

  window.syncCartUI = syncCartUI;

  /** Buka cart sidebar, tutup modal lain, dan sync isi cart. */
  function openCartSidebar() {
    if (!hasCartSidebar) return false;

    $('html, body').addClass('cart-scroll-lock');

    var api = window.chHeaderModals;
    if (api && api.closeOthers) {
      api.closeOthers('cart');
    }

    $('.cart-sidebar-root').addClass('is-open');
    $('.cart-sidebar').addClass('show-sidebar');
    $('#cart-trigger, .header__icon--cart').attr('aria-expanded', 'true');
    syncCartUI();
    return true;
  }

  window.openCartSidebar = openCartSidebar;

  /** Sync cart terlebih dahulu lalu buka cart sidebar. */
  function openCartSidebarAfterSync() {
    if (!hasCartSidebar) return Promise.resolve(false);

    return syncCartUI().then(function () {
      $('html, body').addClass('cart-scroll-lock');

      var api = window.chHeaderModals;
      if (api && api.closeOthers) {
        api.closeOthers('cart');
      }

      $('.cart-sidebar-root').addClass('is-open');
      $('.cart-sidebar').addClass('show-sidebar');
      $('#cart-trigger, .header__icon--cart').attr('aria-expanded', 'true');
      return true;
    });
  }

  window.openCartSidebarAfterSync = openCartSidebarAfterSync;

  /**
   * Pasang event cart: buka trigger, tutup, ubah qty (+/-), pubsub cartUpdate.
   */
  function bindCartEvents() {
    /** Klik icon bag → tutup modal lain, buka cart, sync isi */
    $(document).on('click', '#cart-trigger, .header__icon--cart', function (e) {
      e.preventDefault();
      openCartSidebar();
    });

    /** Klik close / overlay → tutup cart */
    $(document).on('click', '.cart-sidebar-close, .cart-sidebar-overlay', function (e) {
      e.preventDefault();
      closeCartSidebar();
    });

    /** Klik +/- qty → POST /cart/change.js lalu refresh UI */
    $(document).on('click', '.change-item', function () {
      var variantId = $(this).attr('data-variant-id');
      var qty = parseInt($(this).attr('data-quantity'), 10);

      if ($(this).hasClass('change-item-plus')) {
        qty += 1;
      } else if ($(this).hasClass('change-item-minus')) {
        qty -= 1;
      }

      $.ajax({
        type: 'POST',
        url: '/cart/change.js',
        dataType: 'json',
        data: {
          id: parseFloat(variantId),
          quantity: qty,
        },
        success: function () {
          syncCartUI();
        },
      });
    });

    /** Dawn pubsub: refresh cart saat ada perubahan dari komponen lain */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        syncCartUI();
      });
    }
  }

  /* ——— Search sidebar ——— */
  var hasSearchSidebar = $('#search-sidebar').length > 0;
  var searchXhr = null; /* request predictive search aktif (untuk abort) */

  /**
   * Tutup search sidebar + reset state UI.
   */
  function closeSearchSidebar() {
    $('.search-sidebar-root').removeClass('is-open');
    $('.search-sidebar-overlay').removeClass('is-visible');
    $('.search-sidebar').removeClass('show-sidebar');
    $('html, body').removeClass('search-scroll-lock');
    $('.site-header__search').attr('aria-expanded', 'false');
    resetSearchSidebar(true);
  }

  /** Cek apakah search sidebar sedang terbuka */
  function isSearchOpen() {
    return $('.search-sidebar').hasClass('show-sidebar');
  }

  /* Daftarkan search ke API global chHeaderModals */
  window.chHeaderModals.closeSearch = closeSearchSidebar;
  window.chHeaderModals.isSearchOpen = isSearchOpen;

  /**
   * Set class is-no-result + disable tombol submit saat tidak ada hasil.
   * @param {boolean} isNoResult
   */
  function setSearchNoResultState(isNoResult) {
    $('#search-sidebar-form-box').toggleClass('is-no-result', !!isNoResult);
    $('.search-sidebar-form .search-button').prop('disabled', !!isNoResult);
  }

  /**
   * Reset UI search ke state awal (best sellers / latest drops).
   * @param {boolean} clearInput — kosongkan input query
   */
  function resetSearchSidebar(clearInput) {
    if (clearInput) {
      $('#search-input').val('');
    }
    $('#search-result-list').empty();
    $('#search-sidebar-result .term, #search-sidebar-result-not-found .term').text('');
    $('#search-sidebar-body-init').show();
    $('#search-sidebar-result, #search-sidebar-result-not-found').attr('hidden', 'hidden');
    setSearchNoResultState(false);

    var $viewAll = $('#search-result-view-all-link');
    if ($viewAll.length) {
      var defaultHref = $viewAll.data('default-href');
      if (!defaultHref) {
        defaultHref = $viewAll.attr('href');
        $viewAll.data('default-href', defaultHref);
      }
      $viewAll.attr('href', defaultHref);
    }
  }

  var SEARCH_SWATCH_API_URL = 'https://on-running.devbdd.com/config/getProductSame_db';
  var searchResultSwatchCountCache = {};
  var searchResultSwatchPending = {};
  var searchResultRenderSeq = 0;

  /** Ambil handle produk dari object hasil predictive search. */
  function getProductHandleFromSearchProduct(product) {
    if (product && product.handle) return String(product.handle);
    var url = product && product.url ? String(product.url) : '';
    var match = url.match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : '';
  }

  /** Hitung jumlah warna produk search dari sumber snippet swatch. */
  function fetchSwatchCountBySnippetSource(product) {
    var handle = getProductHandleFromSearchProduct(product);
    if (!handle) return Promise.resolve(0);
    if (typeof searchResultSwatchCountCache[handle] !== 'undefined') {
      return Promise.resolve(searchResultSwatchCountCache[handle]);
    }
    if (searchResultSwatchPending[handle]) {
      return searchResultSwatchPending[handle];
    }

    var title = String((product && product.title) || '');
    var payload = {
      product_title: title.replace(/melissa /gi, ''),
      product_fulltitle: title,
      handle: handle,
      shop: typeof Shopify !== 'undefined' ? Shopify.shop : '',
      only_color: true,
    };

    searchResultSwatchPending[handle] = new Promise(function (resolve) {
      $.ajax({
        type: 'POST',
        url: SEARCH_SWATCH_API_URL,
        data: payload,
        crossDomain: true,
      })
        .done(function (response) {
          var data = response;
          if (typeof response === 'string') {
            try {
              data = JSON.parse(response);
            } catch (_err) {
              data = null;
            }
          }
          var dataSame = data && data.data_same ? data.data_same : [];
          var arr = Array.isArray(dataSame) ? dataSame : Object.values(dataSame || {});
          var count = 0;
          var i;

          for (i = 0; i < arr.length; i++) {
            if (arr[i] && arr[i].status !== 'draft') count += 1;
          }

          searchResultSwatchCountCache[handle] = count;
          resolve(count);
        })
        .fail(function () {
          searchResultSwatchCountCache[handle] = 0;
          resolve(0);
        })
        .always(function () {
          delete searchResultSwatchPending[handle];
        });
    });

    return searchResultSwatchPending[handle];
  }

  /**
   * HTML satu tile produk di grid hasil predictive search.
   * @param {object} product
   * @returns {string}
   */

  function buildSearchResultItem(product) {
    var url = escapeHtml(product.url || '');
    var image = escapeHtml(product.image || '');
    var title = escapeHtml(product.title || '');
    var price = escapeHtml(
      product.price
        ? 'IDR ' + Number(product.price).toLocaleString('id-ID')
        : ''
    );
    var handle = escapeHtml(getProductHandleFromSearchProduct(product));

    return (
      '<div class="search-result-item">' +
      '<a href="' + url + '">' +
      '<img src="' + image + '" alt="' + title + '" loading="lazy" width="200" height="200">' +
      '</a>' +
      '</div>' +

      '<div class="search-result-item mobile-only">' +
      '<a href="' + url + '">' +
      '<img src="' + image + '" loading="lazy" width="200" height="200">' +
      '</a>' +
      '<div class="search-result-item-content">' +
      '<div class="search-result-item-title">' + title + '</div>' +
      '<div class="search-result-item-color-count" data-product-handle="' + handle + '">0 Colour[s]</div>' +
      '<div class="search-result-item-price">' + price + '</div>' +
      '</div>' +
      '</div>'
    );
  }

  /** Update label jumlah warna pada hasil search yang masih aktif. */
  function syncSearchResultColorCounts(products, renderSeq) {
    var i;

    for (i = 0; i < products.length; i++) {
      (function (product) {
        var handle = getProductHandleFromSearchProduct(product);
        if (!handle) return;

        fetchSwatchCountBySnippetSource(product).then(function (count) {
          if (renderSeq !== searchResultRenderSeq) return;
          $('#search-result-list .search-result-item-color-count[data-product-handle="' + handle + '"]')
            .text(String(count) + ' Colour[s]');
        });
      })(products[i]);
    }
  }

  /**
   * Tampilkan grid hasil search + link "view all".
   * @param {string} query
   * @param {object[]} products
   */
  function showSearchResults(query, products) {
    var html = '';
    var i;
    var renderSeq;

    for (i = 0; i < products.length; i++) {
      html += buildSearchResultItem(products[i]);
    }

    searchResultRenderSeq += 1;
    renderSeq = searchResultRenderSeq;
    $('#search-result-list').html(html);
    syncSearchResultColorCounts(products, renderSeq);
    $('#search-result-view-all-link').attr(
      'href',
      '/search?type=product&q=' + encodeURIComponent(query)
    );
    $('#search-sidebar-result .term').text(query);
    $('#search-sidebar-body-init').hide();
    $('#search-sidebar-result-not-found').attr('hidden', 'hidden');
    $('#search-sidebar-result').removeAttr('hidden');
    setSearchNoResultState(false);
  }

  /** Tampilkan pesan tidak ada hasil */
  function showSearchNoResults(query) {
    $('#search-result-list').empty();
    $('#search-sidebar-result-not-found .term').text(query);
    $('#search-sidebar-body-init').hide();
    $('#search-sidebar-result').attr('hidden', 'hidden');
    $('#search-sidebar-result-not-found').removeAttr('hidden');
    setSearchNoResultState(true);
  }

  /**
   * Fetch predictive search API Shopify (/search/suggest.json).
   * Caller biasanya memanggil saat query > 2 karakter.
   * @param {string} query
   */
  function fetchSearchSuggestions(query) {
    if (searchXhr && searchXhr.readyState !== 4) {
      searchXhr.abort();
    }

    searchXhr = $.ajax({
      url: 'https://' + window.location.hostname + '/search/suggest.json',
      method: 'GET',
      data: {
        q: query,
        'resources[type]': 'product',
        'resources[limit]': 12,
        'resources[options][unavailable_products]': 'hide',
      },
      dataType: 'json',
    });

    /** Handler sukses: tampilkan hasil atau state kosong */
    searchXhr.done(function (response) {
      var products =
        response &&
          response.resources &&
          response.resources.results &&
          response.resources.results.products
          ? response.resources.results.products
          : [];

      if (products.length > 0) {
        showSearchResults(query, products);
      } else {
        showSearchNoResults(query);
      }
    });

    /** Handler gagal: abaikan abort, tampilkan no results untuk error lain */
    searchXhr.fail(function (_xhr, status) {
      if (status === 'abort') return;
      showSearchNoResults(query);
    });
  }

  /**
   * Pasang event search: buka trigger, tutup, keyup predictive, submit, enter.
   */
  function bindSearchEvents() {
    if (!hasSearchSidebar) return;

    /** Klik icon search di header → tutup modal lain, buka search sidebar */
    $(document).on('click', '.site-header__search', function (e) {
      e.preventDefault();

      var api = window.chHeaderModals;
      if (api && api.closeOthers) {
        api.closeOthers('search');
      }

      $('.search-sidebar-root').addClass('is-open');
      $('.search-sidebar-overlay').addClass('is-visible');
      $('.search-sidebar').addClass('show-sidebar');
      $('html, body').addClass('search-scroll-lock');
      $('.site-header__search').attr('aria-expanded', 'true');

      window.setTimeout(function () {
        $('#search-input').trigger('focus');
      }, 300);
    });

    /** Klik close / overlay → tutup search */
    $(document).on('click', '.search-sidebar-close, .search-sidebar-overlay', function (e) {
      e.preventDefault();
      closeSearchSidebar();
    });

    /** Ketik di input → predictive search atau reset ke state awal */
    $(document).on('keyup', '#search-input', function () {
      var query = $.trim($(this).val());

      if (query.length > 2) {
        fetchSearchSuggestions(query);
      } else {
        resetSearchSidebar(false);
      }
    });

    $(document).on('submit', '.search-sidebar-form', function (e) {
      var query = $.trim($('#search-input').val());
      var isNoResult = $('#search-sidebar-form-box').hasClass('is-no-result');

      if (!query || isNoResult) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    /** Enter di input → blok submit jika query kosong atau no-result */
    $(document).on('keydown', '#search-input', function (e) {
      if (e.key !== 'Enter' && e.keyCode !== 13) return;

      var query = $.trim($(this).val());
      var isNoResult = $('#search-sidebar-form-box').hasClass('is-no-result');

      if (!query || isNoResult) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });
  }

  /* =========================================================
     Collection facets popup — CSS sticky + hide/reveal bareng header
     ========================================================= */

  // Elemen & state sticky toolbar collection.
  var collectionFacetsPopupEl = null;
  var collectionFacetsPopupBounds = { top: 0, bottom: 0 };
  var collectionHeaderBounds = { top: 0, bottom: 0 };
  var collectionFacetsPopupScrollTop = 0;
  var collectionFacetsPopupScrollHandler = null;
  var collectionFacetsPopupResizeHandler = null;
  var collectionFacetsPopupHeaderObserver = null;

  // Ambil aside toolbar collection / search.
  function getCollectionFacetsPopup() {
    return document.getElementById('main-collection-filters')
      || document.getElementById('main-search-filters')
      || document.querySelector('.collection-facets-popup');
  }

  // Ambil header aktif untuk mobile/desktop; beberapa state sticky dipasang di wrapper section.
  function getCollectionStickyHeader() {
    var headers = Array.prototype.slice.call(document.querySelectorAll('sticky-header.header-wrapper, sticky-header'));
    return headers.find(function (header) {
      var rect = header.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }) || document.querySelector('.header-wrapper');
  }

  // Ambil section header Shopify tempat class sticky/hidden dipasang.
  function getCollectionHeaderSection() {
    var stickyHeader = getCollectionStickyHeader();
    return stickyHeader ? stickyHeader.closest('.shopify-section') || stickyHeader : null;
  }

  // Baca tinggi header dari CSS variable theme, lalu fallback ke header mobile yang terlihat.
  function getHeaderHeight() {
    var cssHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    if (!isNaN(cssHeight) && cssHeight > 0) return cssHeight;

    var stickyHeader = getCollectionStickyHeader();
    if (stickyHeader) {
      var rect = stickyHeader.getBoundingClientRect();
      if (rect.height > 0) return rect.height;
    }

    return 0;
  }

  // Simpan batas scroll header (top/bottom) untuk mirror logika StickyHeader.
  function cacheCollectionHeaderBounds() {
    var section = getCollectionHeaderSection();
    if (!section) return;

    var rect = section.getBoundingClientRect();
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    collectionHeaderBounds = {
      top: rect.top + scrollTop,
      bottom: rect.bottom + scrollTop
    };
  }

  // Simpan posisi awal toolbar di dokumen (sebelum sticky/pinned).
  function cacheCollectionFacetsPopupBounds() {
    if (!collectionFacetsPopupEl) return;
    if (collectionFacetsPopupEl.classList.contains('collection-facets-popup--hidden')) return;

    collectionFacetsPopupBounds.top = collectionFacetsPopupEl.getBoundingClientRect().top
      + (window.pageYOffset || document.documentElement.scrollTop);
  }

  // Set CSS variable tinggi toolbar + sticky top (ikut hide/show header).
  function setCollectionFacetsBarHeight() {
    if (!collectionFacetsPopupEl) return;

    var wasHidden = collectionFacetsPopupEl.classList.contains('collection-facets-popup--hidden');
    if (wasHidden) collectionFacetsPopupEl.classList.remove('collection-facets-popup--hidden');

    var height = collectionFacetsPopupEl.getBoundingClientRect().height;
    var headerHeight = getHeaderHeight();
    var hideOffset = height + headerHeight;

    document.documentElement.style.setProperty('--collection-header-height', headerHeight + 'px');
    document.documentElement.style.setProperty('--collection-facets-popup-height', height + 'px');
    document.documentElement.style.setProperty('--collection-facets-popup-hide-offset', hideOffset + 'px');

    if (wasHidden) collectionFacetsPopupEl.classList.add('collection-facets-popup--hidden');
    updateCollectionToolbarStickyTop();
  }

  // Sticky top toolbar: 0 saat header hide, tinggi header saat header tampil.
  function updateCollectionToolbarStickyTop() {
    var headerSection = getCollectionHeaderSection();
    var headerHeight = getHeaderHeight();
    var stickyTop = headerHeight;

    if (headerSection && headerSection.classList.contains('shopify-section-header-hidden')) {
      stickyTop = 0;
    }

    document.documentElement.style.setProperty('--collection-header-height', headerHeight + 'px');
    document.documentElement.style.setProperty('--collection-toolbar-sticky-top', stickyTop + 'px');
  }

  // Cek apakah toolbar sudah nempel (sticky) di bawah header.
  function isCollectionFacetsPopupPinned() {
    if (!collectionFacetsPopupEl) return false;

    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var headerHeight = getHeaderHeight();
    var rect = collectionFacetsPopupEl.getBoundingClientRect();

    return scrollTop > collectionFacetsPopupBounds.top
      && rect.top <= headerHeight + 2;
  }

  // Sembunyikan toolbar — slide ke atas pakai class --hidden + animasi.
  function hideCollectionFacetsPopup() {
    if (!collectionFacetsPopupEl) return;
    collectionFacetsPopupEl.classList.add('animate', 'collection-facets-popup--hidden');
  }

  // Tampilkan toolbar saat scroll up (hapus class --hidden + animasi).
  function revealCollectionFacetsPopup() {
    if (!collectionFacetsPopupEl) return;
    collectionFacetsPopupEl.classList.add('animate');
    collectionFacetsPopupEl.classList.remove('collection-facets-popup--hidden');
  }

  // Kembalikan toolbar ke state normal (belum hide, tanpa animasi paksa).
  function resetCollectionFacetsPopup() {
    if (!collectionFacetsPopupEl) return;
    collectionFacetsPopupEl.classList.remove('collection-facets-popup--hidden', 'animate');
  }

  // Sinkron sticky top toolbar dengan class hide/show header.
  function syncCollectionFacetsPopupWithHeader() {
    updateCollectionToolbarStickyTop();
  }

  // Handler scroll: update sticky top saat header hide/show.
  function onCollectionFacetsPopupScroll() {
    if (!collectionFacetsPopupEl) return;
    updateCollectionToolbarStickyTop();
    collectionFacetsPopupScrollTop = window.pageYOffset || document.documentElement.scrollTop;
  }

  // Pantau perubahan class header (MutationObserver) untuk sync hide/reveal.
  function observeCollectionHeaderSection() {
    if (collectionFacetsPopupHeaderObserver) {
      collectionFacetsPopupHeaderObserver.disconnect();
      collectionFacetsPopupHeaderObserver = null;
    }

    var headerSection = getCollectionHeaderSection();
    if (!headerSection || typeof MutationObserver === 'undefined') return;

    collectionFacetsPopupHeaderObserver = new MutationObserver(syncCollectionFacetsPopupWithHeader);
    collectionFacetsPopupHeaderObserver.observe(headerSection, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  // Lepas listener & observer sticky toolbar, reset class sementara.
  function destroyCollectionToolbarScroll() {
    if (collectionFacetsPopupScrollHandler) {
      window.removeEventListener('scroll', collectionFacetsPopupScrollHandler, false);
      collectionFacetsPopupScrollHandler = null;
    }

    if (collectionFacetsPopupResizeHandler) {
      window.removeEventListener('resize', collectionFacetsPopupResizeHandler);
      collectionFacetsPopupResizeHandler = null;
    }

    if (collectionFacetsPopupHeaderObserver) {
      collectionFacetsPopupHeaderObserver.disconnect();
      collectionFacetsPopupHeaderObserver = null;
    }

    if (collectionFacetsPopupEl) {
      collectionFacetsPopupEl.classList.remove('collection-facets-popup--hidden', 'animate');
    }

    collectionFacetsPopupEl = null;
  }

  // Inisialisasi sticky toolbar: ukur bounds, pasang scroll/resize/header observer.
  function initCollectionToolbarScroll() {
    destroyCollectionToolbarScroll();

    collectionFacetsPopupEl = getCollectionFacetsPopup();
    if (!collectionFacetsPopupEl) return false;

    collectionFacetsPopupScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    setCollectionFacetsBarHeight();
    cacheCollectionFacetsPopupBounds();
    cacheCollectionHeaderBounds();
    observeCollectionHeaderSection();

    collectionFacetsPopupScrollHandler = onCollectionFacetsPopupScroll;

    // Recalculate tinggi & bounds saat viewport berubah.
    collectionFacetsPopupResizeHandler = function onCollectionFacetsPopupResize() {
      setCollectionFacetsBarHeight();
      cacheCollectionFacetsPopupBounds();
      cacheCollectionHeaderBounds();
    };

    window.addEventListener('scroll', collectionFacetsPopupScrollHandler, false);
    window.addEventListener('resize', collectionFacetsPopupResizeHandler);
    return true;
  }

  // Tunggu DOM + aset selesai load, lalu init dengan retry jika elemen belum ada.
  function scheduleCollectionToolbarScrollInit(retriesLeft) {
    var retries = typeof retriesLeft === 'number' ? retriesLeft : 8;

    // Coba init; ulangi sampai aside toolbar ketemu atau retry habis.
    var tryInit = function () {
      var ok = initCollectionToolbarScroll();

      if (!ok && retries > 0) {
        setTimeout(function () {
          scheduleCollectionToolbarScrollInit(retries - 1);
        }, 200);
        return;
      }

      if (ok) {
        requestAnimationFrame(function () {
          setCollectionFacetsBarHeight();
          cacheCollectionFacetsPopupBounds();
          cacheCollectionHeaderBounds();
        });
      }
    };

    // Tunggu layout settle (double rAF) setelah window load.
    var runAfterSettled = function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(tryInit);
      });
    };

    if (document.readyState === 'complete') {
      runAfterSettled();
    } else {
      window.addEventListener('load', runAfterSettled, { once: true });
    }
  }

  // Re-init sticky toolbar setelah filter AJAX ganti isi ProductGridContainer.
  window.reinitCollectionToolbarScroll = function reinitCollectionToolbarScroll() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (!initCollectionToolbarScroll()) {
          scheduleCollectionToolbarScrollInit(3);
        }
      });
    });
  };

  /** Update posisi dan lebar scrollbar custom filter collection. */
  function updateCollectionFilterScrollbar(list) {
    if (!list) return;

    var parent = list.closest('.collection-toolbar__left');
    if (!parent) return;

    var visibleItems = Array.prototype.filter.call(list.querySelectorAll('.collection-grid-filters__item'), function (item) {
      return !item.hidden && item.offsetParent !== null;
    });
    parent.classList.toggle('has-filter-scrollbar', visibleItems.length > 4);

    var scrollWidth = list.scrollWidth;
    var clientWidth = list.clientWidth;

    if (!scrollWidth || scrollWidth <= clientWidth) {
      parent.style.setProperty('--collection-filters-scroll-thumb-width', '100%');
      parent.style.setProperty('--collection-filters-scroll-thumb-left', '0px');
      return;
    }

    var thumbWidth = Math.max((clientWidth / scrollWidth) * clientWidth, 24);
    var maxThumbLeft = clientWidth - thumbWidth;
    var maxScrollLeft = scrollWidth - clientWidth;
    var thumbLeft = maxScrollLeft ? (list.scrollLeft / maxScrollLeft) * maxThumbLeft : 0;

    parent.style.setProperty('--collection-filters-scroll-thumb-width', thumbWidth + 'px');
    parent.style.setProperty('--collection-filters-scroll-thumb-left', thumbLeft + 'px');
  }

  /** Pasang scrollbar custom untuk semua list filter collection dalam scope. */
  function initCollectionFilterScrollbars(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll ? scope.querySelectorAll('.collection-grid-filters__list') : [];

    Array.prototype.forEach.call(lists, function (list) {
      updateCollectionFilterScrollbar(list);

      if (list.dataset.collectionScrollbarBound === 'true') return;
      list.dataset.collectionScrollbarBound = 'true';

      list.addEventListener('scroll', function () {
        updateCollectionFilterScrollbar(list);
      }, { passive: true });

      window.addEventListener('resize', function () {
        updateCollectionFilterScrollbar(list);
      });
    });
  }

  window.reinitCollectionFilterScrollbars = function reinitCollectionFilterScrollbars(root) {
    requestAnimationFrame(function () {
      initCollectionFilterScrollbars(root || document);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initCollectionFilterScrollbars(document);
    }, { once: true });
  } else {
    initCollectionFilterScrollbars(document);
  }

  /* Size chart popup */
  /** Ambil elemen popup size chart aktif di halaman product. */
  function getSizeChartPopup() {
    return document.querySelector('[data-size-chart-popup]');
  }

  /** Sinkron panel size chart sesuai tab ADULT/KIDS yang dipilih. */
  function syncSizeChartTabs(popup) {
    if (!popup) return;

    var checkedInput = popup.querySelector('.size-chart__tab-input:checked')
      || popup.querySelector('.size-chart__tab-input');
    if (!checkedInput) return;

    var category = '';
    if (checkedInput.id.indexOf('-adult') !== -1) {
      category = 'adult';
    } else if (checkedInput.id.indexOf('-kids') !== -1) {
      category = 'kids';
    }

    popup.querySelectorAll('.size-chart__panel').forEach(function (panel) {
      cleanSizeChartPanel(panel);
      panel.classList.toggle('is-active', panel.classList.contains('size-chart__panel--' + category));
    });
  }

  /** Hapus heading dan baris judul ADULT SIZE / KIDS SIZE di table size chart. */
  function cleanSizeChartPanel(panel) {
    if (!panel) return;

    panel.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function (heading) {
      heading.remove();
    });

    panel.querySelectorAll('table').forEach(function (table) {
      var firstRow = table.querySelector('tr');
      if (!firstRow) return;

      var title = (firstRow.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (title.indexOf('adult size') !== -1 || title.indexOf('kids size') !== -1) {
        firstRow.remove();
      }
    });
  }

  /** Buka popup size chart dan aktifkan scroll lock halaman. */
  function openSizeChartPopup() {
    var popup = getSizeChartPopup();
    if (!popup) return;

    syncSizeChartTabs(popup);
    popup.removeAttribute('hidden');
    popup.classList.add('is-open');
    document.documentElement.classList.add('size-chart-scroll-lock');
    document.body.classList.add('size-chart-scroll-lock');
  }

  /** Tutup popup size chart dan lepaskan scroll lock halaman. */
  function closeSizeChartPopup() {
    var popup = getSizeChartPopup();
    if (!popup) return;

    popup.classList.remove('is-open');
    popup.setAttribute('hidden', '');
    document.documentElement.classList.remove('size-chart-scroll-lock');
    document.body.classList.remove('size-chart-scroll-lock');

    var referrer = $(popup).data('referrer');
    if (referrer === 'help') {
      $(popup).data('referrer', '');
      $('#HelpCenterMenuMobile').addClass('is-open');
    }
  }

  $(document).on('click', '.main-product-2026__size-chart', function (event) {
    event.preventDefault();
    if ($('#HelpCenterMenuMobile').hasClass('is-open')) {
      $('#HelpCenterMenuMobile').removeClass('is-open');
      $('[data-size-chart-popup]').data('referrer', 'help');
    }
    openSizeChartPopup();
  });

  $(document).on('click', '[data-size-chart-close]', function (event) {
    event.preventDefault();
    closeSizeChartPopup();
  });

  $(document).on('change', '.size-chart__tab-input', function () {
    syncSizeChartTabs(this.closest('[data-size-chart-popup]'));
  });

  $(document).on('keydown', function (event) {
    if (event.key === 'Escape') closeSizeChartPopup();
  });

  /* =========================================================
     Collection infinite scroll
     ========================================================= */

  var infiniteScrollObserver = null;
  var INFINITE_SCROLL_ROOT_MARGIN = '200px 0px';
  var INFINITE_SCROLL_COOLDOWN_MS = 400;

  // Format teks product count — total saja, tanpa "X of Y".
  /** Format teks jumlah produk collection untuk toolbar. */
  function formatCollectionProductCount(loaded, total) {
    var strings = window.collectionProductGridStrings;
    if (!strings) return String(total);

    return strings.countSimple.replace('@@COUNT@@', total);
  }

  /**
   * Sync product count di toolbar (#ProductCount) setelah infinite scroll.
   * @param {string} [gridSelector]
   */
  window.updateCollectionProductGridCount = function (gridSelector) {
    var selector = gridSelector || '#product-grid';
    var grid = document.querySelector(selector);
    var countEl = document.getElementById('ProductCount');
    if (!grid || !countEl) return;

    var total = parseInt(grid.dataset.totalCount, 10);
    if (isNaN(total)) {
      total = parseInt(countEl.dataset.total, 10) || 0;
    }

    var showing = grid.querySelectorAll(':scope > .fc__grid-item').length;
    if (total > 0 && showing > total) showing = total;

    countEl.dataset.loaded = showing;
    countEl.dataset.total = total;
    countEl.textContent = formatCollectionProductCount(showing, total);
  };

  /** Cek apakah sentinel infinite scroll sudah dekat viewport. */
  function isSentinelNearViewport(sentinel) {
    if (!sentinel || !sentinel.getBoundingClientRect) return false;
    var rect = sentinel.getBoundingClientRect();
    return rect.top <= window.innerHeight + 200;
  }

  /** Tandai sentinel infinite scroll siap dipakai observer. */
  function armSentinel(sentinel) {
    if (sentinel && sentinel.isConnected) {
      sentinel.dataset.ready = '1';
    }
  }

  /** Jadwalkan load page berikutnya kalau sentinel masih terlihat. */
  function scheduleLoadIfStillVisible(sentinel) {
    setTimeout(function () {
      if (!sentinel || !sentinel.isConnected || sentinel.dataset.loading === '1') return;
      if (!sentinel.dataset.nextUrl) return;

      if (!isSentinelNearViewport(sentinel)) {
        armSentinel(sentinel);
        return;
      }

      armSentinel(sentinel);
      fetchNextPage(sentinel);
    }, INFINITE_SCROLL_COOLDOWN_MS);
  }

  /** Lepas sentinel infinite scroll dari observer lalu hapus dari DOM. */
  function removeSentinel(sentinel) {
    if (!sentinel) return;
    if (infiniteScrollObserver) {
      infiniteScrollObserver.unobserve(sentinel);
    }
    sentinel.remove();
  }

  /** Fetch halaman collection berikutnya dan append item ke product grid secara berurutan. */
  function fetchNextPage(sentinel) {
    if (!sentinel || !sentinel.isConnected || sentinel.dataset.loading === '1') return;
    if (sentinel.dataset.ready !== '1') return;

    var nextUrl = sentinel.dataset.nextUrl;
    var gridSelector = sentinel.dataset.gridSelector || '#product-grid';
    var sectionId = sentinel.dataset.sectionId;

    if (!nextUrl) {
      removeSentinel(sentinel);
      return;
    }

    if (sentinel.dataset.lastFetchedUrl === nextUrl) {
      removeSentinel(sentinel);
      return;
    }

    sentinel.dataset.ready = '0';
    sentinel.dataset.loading = '1';
    sentinel.dataset.lastFetchedUrl = nextUrl;
    sentinel.classList.add('is-loading');

    var fetchUrl = new URL(nextUrl, window.location.origin);
    if (sectionId) {
      fetchUrl.searchParams.set('section_id', sectionId);
    }

    fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Network response was not ok: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector(gridSelector);
        var currentGrid = document.querySelector(gridSelector);
        var appendedCount = 0;

        if (newGrid && currentGrid) {
          var fragment = document.createDocumentFragment();
          Array.prototype.forEach.call(newGrid.children, function (child) {
            // Hapus kelas animasi agar tidak terjadi glitch visual atau "mantul" saat produk ditambahkan
            child.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--slide-in');
            child.removeAttribute('data-cascade');
            child.removeAttribute('style');
            fragment.appendChild(child.cloneNode(true));
          });
          appendedCount = fragment.childNodes.length;
          if (appendedCount) {
            currentGrid.appendChild(fragment);
          }
        }

        if (!appendedCount) {
          removeSentinel(sentinel);
          return;
        }

        if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.rehydrateInlineScripts) {
          FacetFiltersForm.rehydrateInlineScripts(currentGrid);
        }

        if (window.updateCollectionProductGridCount) {
          window.updateCollectionProductGridCount(gridSelector);
        }

        var nextSentinel = doc.querySelector('[data-infinite-scroll]');
        var nextPageUrl = nextSentinel && nextSentinel.dataset.nextUrl;

        if (nextPageUrl && nextPageUrl !== nextUrl) {
          sentinel.dataset.nextUrl = nextPageUrl;
          sentinel.dataset.loading = '0';
          sentinel.dataset.ready = '1';
          // Langsung fetch halaman berikutnya tanpa menunggu scroll
          fetchNextPage(sentinel);
        } else {
          removeSentinel(sentinel);
        }
      })
      .catch(function (err) {
        console.error('Infinite scroll fetch failed', err);
        sentinel.dataset.loading = '0';
        sentinel.classList.remove('is-loading');
        removeSentinel(sentinel);
      });
  }

  /** Matikan observer/listener infinite scroll yang aktif. */
  function destroyInfiniteScroll() {
    if (infiniteScrollObserver) {
      infiniteScrollObserver.disconnect();
      infiniteScrollObserver = null;
    }
    $(window).off('scroll.infiniteScroll');
  }

  /** Inisialisasi auto-load untuk semua halaman collection secara instan. */
  function initInfiniteScroll() {
    destroyInfiniteScroll();

    var sentinels = document.querySelectorAll('[data-infinite-scroll]');
    if (!sentinels.length) return;

    Array.prototype.forEach.call(sentinels, function (sentinel) {
      sentinel.dataset.ready = '1';
      sentinel.dataset.loading = '0';
      // Mulai auto-load halaman berikutnya langsung setelah load
      fetchNextPage(sentinel);
    });
  }

  window.reinitInfiniteScroll = initInfiniteScroll;

  $(document).on('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-infinite-scroll]')) {
      initInfiniteScroll();
      window.updateCollectionProductGridCount();
    }

    if (event.target && event.target.querySelector('.collection-facets-popup')) {
      window.reinitCollectionToolbarScroll();
    }

    if (
      event.target &&
      (event.target.querySelector('#account-register-sidebar') ||
        event.target.querySelector('.account-sidebar'))
    ) {
      initAccountRegisterOpenOnLoad(event.target);
      scheduleAuthSidebarsOnLoad();
    }
  });

  /* =============================================================================
   * BAGIAN 3: Login sidebar & register sidebar (account-register-sidebar / account-sidebar)
   * ============================================================================= */
  var REGISTER_DRAFT_KEY = 'superga_register_draft';
  var ACCOUNT_SIDEBAR_TRANSITION_MS = 300;
  var registerBirthdayFlatpickr = null;
  var hasAccountSidebar = $('.account-sidebar').length > 0;
  var hasRegisterSidebar = $('#account-register-sidebar').length > 0;

  /** Paksa browser menghitung layout agar animasi drawer bisa restart. */
  function forceReflow(el) {
    if (el) void el.offsetWidth;
  }

  /** Tampilkan register sidebar dengan animasi slide. */
  function showRegisterSidebarAnimated(options) {
    options = options || {};
    var $reg = $('#account-register-sidebar');
    var $regRoot = $('.account-register-sidebar-root');
    var $regOverlay = $('.account-register-sidebar-overlay');

    $reg.removeAttr('hidden').attr('aria-hidden', 'false');
    $regRoot.addClass('is-open');
    $('html, body').addClass('account-scroll-lock');
    if (!options.keepLoginOverlay) {
      $regOverlay.addClass('is-visible');
    }
    $reg.removeClass('show-sidebar');
    forceReflow($reg[0]);

    requestAnimationFrame(function () {
      $reg.addClass('show-sidebar');
      syncAccountDrawerScrollLock();
    });
  }

  /** Tampilkan login sidebar dengan animasi slide. */
  function showLoginSidebarAnimated(options) {
    options = options || {};
    var $login = $('.account-sidebar');
    var $loginRoot = $('.account-sidebar-root');
    var $loginOverlay = $('.account-sidebar-overlay');

    $loginRoot.addClass('is-open');
    $('html, body').addClass('account-scroll-lock');
    if (!options.keepRegisterOverlay) {
      $loginOverlay.addClass('is-visible');
    }
    $login.removeClass('show-sidebar');
    forceReflow($login[0]);

    requestAnimationFrame(function () {
      $login.addClass('show-sidebar');
      syncAccountDrawerScrollLock();
      window.setTimeout(function () {
        $('.account-sidebar-field-input').each(function () {
          updateAccountSidebarFieldButton($(this));
        });
      }, 100);
    });
  }

  /** Sembunyikan login sidebar lalu jalankan callback opsional. */
  function hideLoginSidebarAnimated(onDone) {
    var $login = $('.account-sidebar');
    if (!$login.hasClass('show-sidebar')) {
      $('.account-sidebar-root').removeClass('is-open');
      $('.account-sidebar-overlay').removeClass('is-visible');
      if (onDone) onDone();
      return;
    }

    $login.removeClass('show-sidebar');
    setTimeout(function () {
      $('.account-sidebar-root').removeClass('is-open');
      $('.account-sidebar-overlay').removeClass('is-visible');
      if (onDone) onDone();
    }, ACCOUNT_SIDEBAR_TRANSITION_MS);
  }

  /** Sembunyikan register sidebar lalu jalankan callback opsional. */
  function hideRegisterSidebarAnimated(onDone) {
    var $reg = $('#account-register-sidebar');
    if (!$reg.hasClass('show-sidebar')) {
      $('.account-register-sidebar-root').removeClass('is-open');
      $('.account-register-sidebar-overlay').removeClass('is-visible');
      $reg.attr('aria-hidden', 'true');
      if (onDone) onDone();
      return;
    }

    $reg.removeClass('show-sidebar');
    setTimeout(function () {
      $('.account-register-sidebar-root').removeClass('is-open');
      $('.account-register-sidebar-overlay').removeClass('is-visible');
      $reg.attr('aria-hidden', 'true');
      if (onDone) onDone();
    }, ACCOUNT_SIDEBAR_TRANSITION_MS);
  }

  /** Ambil pathname aman dari URL anchor/link. */
  function getAnchorPath(href) {
    if (!href) return '';
    try {
      var anchor = document.createElement('a');
      anchor.href = href;
      return anchor.pathname || '';
    } catch (err) {
      return '';
    }
  }

  /** Sinkron scroll lock saat login/register sidebar terbuka. */
  function syncAccountDrawerScrollLock() {
    var loginOpen = $('.account-sidebar-root').hasClass('is-open');
    var registerOpen = $('.account-register-sidebar-root').hasClass('is-open');

    if (loginOpen || registerOpen) {
      $('html, body').addClass('account-scroll-lock');
    } else {
      $('html, body').removeClass('account-scroll-lock');
    }
  }

  /** Tutup semua sidebar account dan reset form login/register. */
  function closeAccountSidebars() {
    $('.account-sidebar-root').removeClass('is-open');
    $('.account-sidebar-overlay').removeClass('is-visible');
    $('.account-sidebar').removeClass('show-sidebar');
    $('.account-register-sidebar-root').removeClass('is-open');
    $('.account-register-sidebar-overlay').removeClass('is-visible');
    $('#account-register-sidebar').removeClass('show-sidebar').attr('aria-hidden', 'true');
    resetLoginSidebar();
    resetAccountRegisterSidebar();
    setRegisterSidebarStep(1);
    $('html, body').removeClass('account-scroll-lock');
  }

  /** Cek apakah login atau register sidebar sedang terbuka. */
  function isAccountSidebarOpen() {
    return (
      $('.account-sidebar-root').hasClass('is-open') ||
      $('.account-register-sidebar-root').hasClass('is-open')
    );
  }

  /** Simpan URL saat ini sebagai return_to untuk auth popup. */
  function setRegisterPopupReturnTo() {
    var returnTo = window.location.pathname + window.location.search;
    $('#register-popup-return-to, #account-sidebar-return-to').val(returnTo);
    try {
      localStorage.setItem('account_sidebar_return_url', returnTo);
    } catch (err) { }
  }

  /** Tutup drawer/header lain sebelum membuka popup auth. */
  function closeNavDrawersBeforeAuthPopup() {
    var api = window.chHeaderModals || {};
    if (api.closeOthers) {
      api.closeOthers('account');
    }
  }

  /** Tampilkan error login yang disimpan sementara setelah redirect/reload. */
  function applyLoginPopupErrorFlash() {
    var message = null;
    try {
      message = sessionStorage.getItem('account_login_required_alert');
      if (message) {
        sessionStorage.removeItem('account_login_required_alert');
      }
    } catch (e0) { }

    var messages = [];
    if (message) {
      messages.push(message);
    }

    var raw = null;
    try {
      raw = sessionStorage.getItem('account_login_popup_errors');
    } catch (err) {
      raw = null;
    }
    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          messages = messages.concat(parsed);
        }
        sessionStorage.removeItem('account_login_popup_errors');
      } catch (err2) { }
    }

    if (!messages.length) return false;

    var $error = $('.account-sidebar-error');
    if (!$error.length) return false;

    $error
      .html(
        messages
          .map(function (msg) {
            return '<p>' + $('<div>').text(String(msg)).html() + '</p>';
          })
          .join('')
      )
      .removeClass('is-hidden');
    return true;
  }

  /** Bersihkan error dan kembalikan field login ke state awal. */
  function clearLoginSidebarErrors() {
    $('.account-sidebar-error').addClass('is-hidden').empty();
    $('#account-sidebar-field-password').hide();
    $('#account-sidebar-field-email').show();
    $('.account-sidebar-register-hint').show();
  }

  /** Reset seluruh input dan tombol di login sidebar. */
  function resetLoginSidebar() {
    clearPopupInputs($('.account-sidebar'));
    clearLoginSidebarErrors();
    $('.account-sidebar-btn').prop('disabled', true).attr('disabled', 'disabled');
  }

  /** Enable/disable tombol submit field login berdasarkan panjang input. */
  function updateAccountSidebarFieldButton($input) {
    var query = ($input.val() || '').trim();
    var $button = $input.next('.account-sidebar-btn');

    if (query.length > 2) {
      $button.prop('disabled', false).removeAttr('disabled');
    } else {
      $button.prop('disabled', true).attr('disabled', 'disabled');
    }
  }

  /** Buka login sidebar untuk guest, termasuk transisi dari register sidebar. */
  function openGuestLoginSidebar(options) {
    if (!hasAccountSidebar) return;

    options = options || {};
    if (!options.keepLoginErrors && !options.alertMessage) {
      clearLoginSidebarErrors();
    }

    closeNavDrawersBeforeAuthPopup();
    setRegisterSidebarStep(1);

    if (options.returnTo) {
      $('#register-popup-return-to, #account-sidebar-return-to').val(options.returnTo);
      try {
        localStorage.setItem('account_sidebar_return_url', options.returnTo);
      } catch (errReturn) { }
    } else {
      setRegisterPopupReturnTo();
    }

    var fromRegister = $('#account-register-sidebar').hasClass('show-sidebar');
    if (fromRegister) {
      $('#account-register-sidebar').removeClass('show-sidebar');
      showLoginSidebarAnimated({ keepRegisterOverlay: true });
      setTimeout(function () {
        $('.account-register-sidebar-root').removeClass('is-open');
        $('.account-register-sidebar-overlay').removeClass('is-visible');
        $('#account-register-sidebar').attr('aria-hidden', 'true');
        resetAccountRegisterSidebar();
        $('.account-sidebar-overlay').addClass('is-visible');
      }, ACCOUNT_SIDEBAR_TRANSITION_MS);
    } else {
      $('.account-register-sidebar-root').removeClass('is-open');
      $('.account-register-sidebar-overlay').removeClass('is-visible');
      $('#account-register-sidebar').removeClass('show-sidebar').attr('aria-hidden', 'true');
      resetAccountRegisterSidebar();
      showLoginSidebarAnimated();
    }

    if (options.alertMessage) {
      var $err = $('.account-sidebar-error');
      if ($err.length) {
        $err.html('<p>' + $('<div>').text(options.alertMessage).html() + '</p>').removeClass('is-hidden');
      }
    } else if (options.keepLoginErrors) {
      applyLoginPopupErrorFlash();
    }
    syncAccountDrawerScrollLock();
  }

  /** Validasi sederhana format email register popup. */
  function registerDraftEmailValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
  }

  /** Enable tombol continue step 1 jika email dan password valid. */
  function updateRegisterStep1ButtonState() {
    var email = $('#register-popup-email-input').val();
    var pw = $('#register-popup-password-input').val();
    var ok = registerDraftEmailValid(email) && (pw || '').length >= 5;
    $('#register-popup-step1-continue').prop('disabled', !ok);
  }

  /** Enable tombol submit step 2 jika nama lengkap terisi. */
  function updateRegisterStep2ButtonState() {
    var full = ($('#register-popup-full-name').val() || '').trim();
    $('#register-popup-step2-submit').prop('disabled', full.length === 0);
  }

  /** Salin credential step 1 ke hidden input form register Shopify. */
  function syncRegisterPopupHiddenCredentials() {
    var email = ($('#register-popup-email-input').val() || '').trim();
    var password = $('#register-popup-password-input').val() || '';
    if (email) {
      $('#register-popup-email-hidden').val(email);
    }
    if (password) {
      $('#register-popup-password-hidden').val(password);
    }
  }

  /** Pindah tampilan register sidebar antara step 1 dan step 2. */
  function setRegisterSidebarStep(step) {
    var $sidebar = $('#account-register-sidebar');
    $('#account-register-sidebar-step1, #account-register-sidebar-step2').removeAttr('style');
    if (step === 2) {
      $sidebar.addClass('is-step2');
    } else {
      $sidebar.removeClass('is-step2');
    }
  }

  /** Reset form register sidebar, state step, dan datepicker. */
  function resetAccountRegisterSidebar() {
    setRegisterSidebarStep(1);
    $('.account-register-sidebar-title-step1').removeAttr('hidden');
    $('.account-register-sidebar-title-step2').attr('hidden', true);
    $('#register-popup-email-input').val('');
    $('#register-popup-password-input').val('');
    $('#register-popup-step1-continue').prop('disabled', true);
    $('#register-popup-step2-submit').prop('disabled', true);

    var $regForm = $('#AccountRegisterPopupCreateForm');
    if ($regForm.length && $regForm[0]) {
      $regForm[0].reset();
    }

    updateRegisterStep2ButtonState();
    $(
      '#register-popup-email-hidden, #register-popup-password-hidden, #register-popup-first-name-hidden, #register-popup-last-name-hidden, #register-popup-note-hidden'
    ).val('');

    if (registerBirthdayFlatpickr) {
      try {
        registerBirthdayFlatpickr.destroy();
      } catch (err) { }
      registerBirthdayFlatpickr = null;
    }
  }

  /** Cek apakah ada error register yang harus ditampilkan setelah reload. */
  function hasPendingRegisterErrorReload() {
    try {
      return !!sessionStorage.getItem('account_register_popup_errors');
    } catch (err) {
      return false;
    }
  }

  /** Bersihkan flag session/localStorage untuk auth popup. */
  function clearAuthPopupSessionFlags() {
    try {
      sessionStorage.removeItem('account_register_from_popup_at');
      sessionStorage.removeItem('account_register_open_sidebar_on_load');
      sessionStorage.removeItem('account_register_popup_errors');
      sessionStorage.removeItem('account_login_from_popup_at');
      sessionStorage.removeItem('account_login_popup_errors');
      sessionStorage.removeItem('open_login_sidebar_after_redirect');
      sessionStorage.removeItem('open_register_sidebar_on_load');
      localStorage.removeItem(REGISTER_DRAFT_KEY);
    } catch (err) { }
  }

  /** Buka register sidebar untuk guest, termasuk transisi dari login sidebar. */
  function openGuestRegisterSidebar(options) {
    if (!hasRegisterSidebar) return;

    options = options || {};
    var keepErrors = options.keepErrors || hasPendingRegisterErrorReload();

    if (!keepErrors) {
      try {
        sessionStorage.removeItem('account_register_popup_errors');
        sessionStorage.removeItem('account_register_open_sidebar_on_load');
      } catch (err) { }
      resetAccountRegisterSidebar();
    }

    closeNavDrawersBeforeAuthPopup();
    setRegisterPopupReturnTo();
    setRegisterSidebarStep(1);
    $('.account-register-sidebar-title-step1').removeAttr('hidden');
    $('.account-register-sidebar-title-step2').attr('hidden', true);

    if (options.fromLogin && $('.account-sidebar').hasClass('show-sidebar')) {
      $('.account-sidebar').removeClass('show-sidebar');
      showRegisterSidebarAnimated({ keepLoginOverlay: true });
      setTimeout(function () {
        $('.account-sidebar-root').removeClass('is-open');
        $('.account-sidebar-overlay').removeClass('is-visible');
        resetLoginSidebar();
        $('.account-register-sidebar-overlay').addClass('is-visible');
      }, ACCOUNT_SIDEBAR_TRANSITION_MS);
    } else {
      $('.account-sidebar-root').removeClass('is-open');
      $('.account-sidebar-overlay').removeClass('is-visible');
      $('.account-sidebar').removeClass('show-sidebar');
      resetLoginSidebar();
      showRegisterSidebarAnimated();
    }

    syncAccountDrawerScrollLock();
  }

  /** Buat HTML pesan error register untuk ditampilkan di sidebar. */
  function buildRegisterPopupErrorHtml(messages) {
    if (!messages || !messages.length) return '';
    var items = messages
      .map(function (msg) {
        return (
          '<p class="account-register-sidebar-form-errors-message">' +
          $('<div>').text(String(msg)).html() +
          '</p>'
        );
      })
      .join('');
    return (
      '<span id="account-register-open-on-load" hidden></span>' +
      '<div class="account-register-sidebar-form-errors" tabindex="-1" role="alert">' +
      '<p class="account-register-sidebar-form-errors-heading">Please adjust the following:</p>' +
      items +
      '</div>'
    );
  }

  /** Tampilkan error register yang disimpan sementara setelah redirect/reload. */
  function applyRegisterPopupErrorFlash() {
    var raw = null;
    try {
      raw = sessionStorage.getItem('account_register_popup_errors');
    } catch (err) {
      return false;
    }
    if (!raw) return false;

    var messages = [];
    try {
      messages = JSON.parse(raw);
      sessionStorage.removeItem('account_register_popup_errors');
    } catch (err2) {
      return false;
    }
    if (!messages.length) return false;

    var $step1 = $('#account-register-sidebar-step1');
    if (!$step1.length) return false;

    $step1.find('.account-register-sidebar-form-errors, #account-register-open-on-load').remove();
    $step1.prepend(buildRegisterPopupErrorHtml(messages));
    return true;
  }

  /** Pastikan fallback error register muncul bila session flag tersedia. */
  function ensureRegisterPopupFallbackError() {
    var $step1 = $('#account-register-sidebar-step1');
    if (!$step1.length || $step1.find('.account-register-sidebar-form-errors').length) return;

    var hasErrors = false;
    try {
      hasErrors = !!sessionStorage.getItem('account_register_popup_errors');
    } catch (err) {
      return;
    }
    if (!hasErrors) return;

    if (!$('#account-register-open-on-load').length) {
      $step1.prepend('<span id="account-register-open-on-load" hidden></span>');
    }
    $step1.prepend(
      buildRegisterPopupErrorHtml(['This email address is already associated with an account.'])
    );
  }

  /** Buka register sidebar langsung ke state error setelah submit gagal. */
  function openAccountRegisterSidebarWithErrors() {
    if (!hasRegisterSidebar) return;

    ensureRegisterPopupFallbackError();

    var draft = null;
    try {
      draft = JSON.parse(localStorage.getItem(REGISTER_DRAFT_KEY) || 'null');
    } catch (err) { }

    $('.account-sidebar-root').removeClass('is-open');
    $('.account-sidebar-overlay').removeClass('is-visible');
    $('.account-sidebar').removeClass('show-sidebar');
    showRegisterSidebarAnimated();
    setRegisterSidebarStep(1);
    $('.account-register-sidebar-title-step1').removeAttr('hidden');
    $('.account-register-sidebar-title-step2').attr('hidden', true);

    if (draft && draft.email) {
      $('#register-popup-email-input').val(draft.email);
      $('#register-popup-email-hidden').val(draft.email);
    }
    if (draft && draft.password) {
      $('#register-popup-password-input').val(draft.password);
      $('#register-popup-password-hidden').val(draft.password);
    }

    updateRegisterStep1ButtonState();
    syncAccountDrawerScrollLock();

    var $errors = $('#account-register-sidebar-step1 .account-register-sidebar-form-errors').first();
    if ($errors.length) {
      $errors[0].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  /** Buka register sidebar otomatis saat halaman load jika ada marker/error. */
  function initAccountRegisterOpenOnLoad(root) {
    var scope = root && root.querySelectorAll ? root : document;

    applyRegisterPopupErrorFlash();
    ensureRegisterPopupFallbackError();

    var shouldOpen = false;
    try {
      if (sessionStorage.getItem('account_register_open_sidebar_on_load') === '1') {
        shouldOpen = !!sessionStorage.getItem('account_register_popup_errors');
        sessionStorage.removeItem('account_register_open_sidebar_on_load');
      }
      sessionStorage.removeItem('account_register_from_popup_at');
    } catch (err) { }

    scope.querySelectorAll('#account-register-sidebar[data-register-open-on-load="true"]').forEach(function () {
      shouldOpen = true;
    });

    if (
      scope.getElementById('account-register-open-on-load') ||
      scope.querySelector('#account-register-sidebar-step1 .account-register-sidebar-form-errors')
    ) {
      shouldOpen = true;
    }

    if (!shouldOpen) return;
    openAccountRegisterSidebarWithErrors();
  }

  /** Jadwalkan pembukaan login/register sidebar berdasarkan flag session. */
  function scheduleAuthSidebarsOnLoad() {
    if (window.isCustomerLoggedIn) {
      clearAuthPopupSessionFlags();
      return;
    }

    if (hasPendingRegisterErrorReload()) {
      try {
        sessionStorage.removeItem('open_login_sidebar_after_redirect');
      } catch (err) { }
      setTimeout(function () {
        initAccountRegisterOpenOnLoad(document);
      }, 0);
      return;
    }

    try {
      if (sessionStorage.getItem('open_register_sidebar_on_load') === '1') {
        sessionStorage.removeItem('open_register_sidebar_on_load');
        setTimeout(function () {
          openGuestRegisterSidebar();
        }, 0);
        return;
      }
      if (sessionStorage.getItem('open_login_sidebar_after_redirect') === '1') {
        sessionStorage.removeItem('open_login_sidebar_after_redirect');
        setTimeout(function () {
          openGuestLoginSidebar({ keepLoginErrors: true });
        }, 0);
      }
    } catch (err2) { }
  }

  /** Pasang semua event klik/input/submit untuk login dan register sidebar. */
  function bindAccountSidebarEvents() {
    $(document).on('click', '.site-header__account, .header__icon--account', function (e) {
      if ($(this).hasClass('is-customer') || window.isCustomerLoggedIn) {
        return;
      }

      e.preventDefault();
      closeNavDrawersBeforeAuthPopup();
      openGuestLoginSidebar();
    });

    $(document).on('click', '.account-sidebar-close, .account-sidebar-overlay', function () {
      $('.account-sidebar-root').removeClass('is-open');
      $('.account-sidebar-overlay').removeClass('is-visible');
      $('.account-sidebar').removeClass('show-sidebar');
      resetLoginSidebar();
      if (!$('.account-register-sidebar-root').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock');
      }
    });

    $(document).on('keyup input change paste', '.account-sidebar-field-input', function () {
      var input = this;

      window.setTimeout(function () {
        updateAccountSidebarFieldButton($(input));
      }, 0);
    });

    $(document).on('click', '.account-sidebar-btn-continue', function () {
      var $parent = $(this).closest('.account-sidebar-field');
      $parent.hide();
      $parent.next().show();
      $('.account-sidebar-register-hint').hide();
      $parent.next().find('.account-sidebar-field-input').trigger('focus');
    });

    $(document).on('click', '.account-sidebar-open-register', function (e) {
      e.preventDefault();
      if (!hasRegisterSidebar) return;
      try {
        sessionStorage.setItem('account_auth_from_login_flow', '1');
      } catch (err) { }
      openGuestRegisterSidebar({ fromLogin: true });
    });

    $(document).on('submit', '#AccountSidebarLoginForm', function () {
      setRegisterPopupReturnTo();
      $(this).find('button[type="submit"]').prop('disabled', true).text('LOGGING IN...');
      try {
        sessionStorage.setItem('account_login_from_popup_at', String(Date.now()));
      } catch (err) { }
    });

    $(document).on('click', '.js-open-login-sidebar, .cart__login-paragraph a', function (e) {
      if (window.isCustomerLoggedIn) return;
      e.preventDefault();
      closeNavDrawersBeforeAuthPopup();
      openGuestLoginSidebar();
    });

    $(document).on('click', 'a[href]', function (e) {
      if (window.isCustomerLoggedIn) return;
      var $link = $(this);
      if ($link.hasClass('is-customer') || $link.closest('form').length) return;
      if (
        $link.hasClass('account-sidebar-open-register') ||
        $link.hasClass('js-open-login-sidebar') ||
        $link.hasClass('mobile-nav-sidebar-sign-in') ||
        $link.hasClass('site-header__account') ||
        $link.hasClass('header__icon--account')
      ) {
        return;
      }

      var href = $link.attr('href') || '';
      var path = getAnchorPath(href);
      if (!path) return;

      if (path === '/account/login' || href.indexOf('/account/login') !== -1) {
        e.preventDefault();
        closeNavDrawersBeforeAuthPopup();
        openGuestLoginSidebar();
        return;
      }
      if (path === '/account/register' || href.indexOf('/account/register') !== -1) {
        e.preventDefault();
        closeNavDrawersBeforeAuthPopup();
        openGuestRegisterSidebar();
        return;
      }
      if (path === '/pages/your-purchase' || href.indexOf('/pages/your-purchase') !== -1) {
        e.preventDefault();
        closeNavDrawersBeforeAuthPopup();
        openGuestLoginSidebar({
          alertMessage: 'Please sign in to view your purchases.',
          returnTo: '/pages/your-purchase'
        });
        return;
      }
      if (path === '/account') {
        e.preventDefault();
        closeNavDrawersBeforeAuthPopup();
        openGuestLoginSidebar();
      }
    });

    $(document).on('click', '.account-register-sidebar-close, .account-register-sidebar-overlay', function () {
      $('.account-register-sidebar-root').removeClass('is-open');
      $('.account-register-sidebar-overlay').removeClass('is-visible');
      $('#account-register-sidebar').removeClass('show-sidebar').attr('aria-hidden', 'true');
      resetAccountRegisterSidebar();
      if (!$('.account-sidebar-root').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock');
      }
    });

    $(document).on(
      'keyup change input',
      '#register-popup-email-input, #register-popup-password-input',
      function () {
        updateRegisterStep1ButtonState();
      }
    );

    $(document).on(
      'keyup change input',
      '#register-popup-full-name, #register-popup-phone, #AccountRegisterPopup-birthday',
      function () {
        updateRegisterStep2ButtonState();
      }
    );

    $(document).on('click', '#register-popup-step1-continue', function () {
      var email = $('#register-popup-email-input').val().trim();
      var password = $('#register-popup-password-input').val();
      if (!registerDraftEmailValid(email) || (password || '').length < 5) {
        return;
      }

      try {
        localStorage.setItem(
          REGISTER_DRAFT_KEY,
          JSON.stringify({
            email: email,
            password: password,
            savedAt: Date.now(),
          })
        );
      } catch (err) { }

      syncRegisterPopupHiddenCredentials();
      setRegisterPopupReturnTo();

      setRegisterSidebarStep(2);
      $('.account-register-sidebar-title-step1').attr('hidden', true);
      $('.account-register-sidebar-title-step2').removeAttr('hidden');

      if (
        typeof flatpickr !== 'undefined' &&
        $('#AccountRegisterPopup-birthday').length &&
        !registerBirthdayFlatpickr
      ) {
        registerBirthdayFlatpickr = flatpickr('#AccountRegisterPopup-birthday', {
          dateFormat: 'd/m/Y',
          disableMobile: true,
          altInput: false,
          onChange: function () {
            updateRegisterStep2ButtonState();
          },
        });
      }
      updateRegisterStep2ButtonState();
    });

    $(document).on('submit', '#AccountRegisterPopupCreateForm', function (e) {
      var isStep2 = $('#account-register-sidebar').hasClass('is-step2');

      if (!isStep2) {
        e.preventDefault();
        var step1Email = $('#register-popup-email-input').val().trim();
        var step1Password = $('#register-popup-password-input').val();
        if (registerDraftEmailValid(step1Email) && (step1Password || '').length >= 5) {
          $('#register-popup-step1-continue').trigger('click');
        }
        return false;
      }

      $('#register-popup-return-to').val('/account');
      syncRegisterPopupHiddenCredentials();
      try {
        sessionStorage.setItem('account_register_from_popup_at', String(Date.now()));
      } catch (err) { }

      var full = $('#register-popup-full-name').val().trim();
      if (!full) {
        e.preventDefault();
        updateRegisterStep2ButtonState();
        return false;
      }

      var email = (
        $('#register-popup-email-hidden').val() ||
        $('#register-popup-email-input').val() ||
        ''
      ).trim();
      var password =
        $('#register-popup-password-hidden').val() || $('#register-popup-password-input').val() || '';
      if (!registerDraftEmailValid(email) || (password || '').length < 5) {
        e.preventDefault();
        setRegisterSidebarStep(1);
        $('.account-register-sidebar-title-step1').removeAttr('hidden');
        $('.account-register-sidebar-title-step2').attr('hidden', true);
        if (email) {
          $('#register-popup-email-input').val(email);
        }
        if (password) {
          $('#register-popup-password-input').val(password);
        }
        updateRegisterStep1ButtonState();
        return false;
      }

      var parts = full.split(/\s+/);
      var firstName = parts.shift() || '';
      var lastName = parts.join(' ') || firstName;
      $('#register-popup-first-name-hidden').val(firstName);
      $('#register-popup-last-name-hidden').val(lastName);

      var phone = $('#register-popup-phone').val().trim();
      var birthday = $('#AccountRegisterPopup-birthday').val().trim();
      var noteParts = [];
      if (phone) {
        noteParts.push('Phone: ' + phone);
      }
      if (birthday) {
        noteParts.push('Birthday: ' + birthday);
      }
      $('#register-popup-note-hidden').val(noteParts.join(' | '));
      syncRegisterPopupHiddenCredentials();
    });
  }

  window.chHeaderModals.closeAccountSidebars = closeAccountSidebars;
  window.chHeaderModals.isAccountOpen = isAccountSidebarOpen;
  window.openGuestLoginSidebar = openGuestLoginSidebar;
  window.openGuestRegisterSidebar = openGuestRegisterSidebar;

  /* ——— Overlay material ——— */
  var hasOverlayMaterial = $('#overlay-material, #overlay-material-mobile').length > 0;

  /** Ambil root overlay material sesuai breakpoint aktif. */
  function getActiveOverlayMaterialRoot() {
    var isMobile = window.matchMedia && window.matchMedia('(max-width: 990px)').matches;
    var selector = isMobile
      ? '.overlay-material-root--mobile'
      : '.section-overlay-material .overlay-material-root';
    var root = document.querySelector(selector);
    return root || document.querySelector('.overlay-material-root');
  }

  /** Tutup overlay material dan bersihkan scroll/Swiper custom. */
  function closeOverlayMaterial() {
    if (typeof window.destroyOverlayMaterialScroll === 'function') {
      window.destroyOverlayMaterialScroll();
    } else if (typeof window.destroyOverlayMaterialSwiper === 'function') {
      window.destroyOverlayMaterialSwiper();
    }
    $('.overlay-material-root').removeClass('is-open');
    $('.overlay-material').removeClass('show-overlay');
    $('html, body').removeClass('overlay-material-scroll-lock');
    $('.js-overlay-material-trigger').attr('aria-expanded', 'false');
  }

  /** Cek apakah overlay material sedang terbuka. */
  function isOverlayMaterialOpen() {
    return $('.overlay-material').hasClass('show-overlay');
  }

  /** Buka overlay material desktop/mobile dan init scroll jika perlu. */
  function openOverlayMaterial() {
    var api = window.chHeaderModals;
    if (api && api.closeOthers) {
      api.closeOthers('overlay-material');
    }

    $('html, body').addClass('overlay-material-scroll-lock');
    var root = getActiveOverlayMaterialRoot();
    if (!root) return;

    $(root).addClass('is-open');
    $(root).find('.overlay-material').addClass('show-overlay');
    $('.js-overlay-material-trigger').attr('aria-expanded', 'true');

    if (root.classList.contains('overlay-material-root--mobile')) return;

    window.setTimeout(function () {
      if (typeof window.initOverlayMaterialScroll !== 'function') return;
      window.initOverlayMaterialScroll(root);
    }, 400);
  }

  window.chHeaderModals.closeOverlayMaterial = closeOverlayMaterial;
  window.chHeaderModals.isOverlayMaterialOpen = isOverlayMaterialOpen;

  /** Pasang event buka/tutup overlay material. */
  function bindOverlayMaterialEvents() {
    if (!hasOverlayMaterial) return;

    $(document).on(
      'click',
      '.js-overlay-material-trigger, a[href="#SeeHowIt\'sMade"], a[href="#SeeHowIt’sMade"], a[href="#SeeHowItsMade"], a[href="#SheHowItsMade"], a[href="#seehowitsmade"], a[href="#overlay-material"], a[href="#overlaymaterial"], a[href*="SeeHowIt"][href*="Made"], a[href*="seehowit"][href*="made"]',
      function (e) {
        e.preventDefault();
        openOverlayMaterial();
      }
    );

    $(document).on('click', '.overlay-material__close, .overlay-material-overlay', function (e) {
      e.preventDefault();
      closeOverlayMaterial();
    });

    $(document).on('keydown', function (e) {
      if (e.key === 'Escape' && isOverlayMaterialOpen()) {
        closeOverlayMaterial();
      }
    });
  }

  /* ——— Superga Care ——— */
  var hasSupergaCare = $('#superga-care, #superga-care-mobile').length > 0;
  var supergaCareScrollY = 0;

  function getActiveSupergaCareRoot() {
    var isMobile = window.matchMedia && window.matchMedia('(max-width: 990px)').matches;
    var selector = isMobile
      ? '.superga-care-root--mobile'
      : '.superga-care-root:not(.superga-care-root--mobile)';
    var root = document.querySelector(selector);
    return root || document.querySelector('.superga-care-root');
  }

  function lockSupergaCarePageScroll() {
    if (document.body.classList.contains('superga-care-scroll-lock')) return;
    supergaCareScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    $('html, body').addClass('superga-care-scroll-lock');
    document.body.style.top = '-' + supergaCareScrollY + 'px';
  }

  function unlockSupergaCarePageScroll() {
    if (!document.body.classList.contains('superga-care-scroll-lock')) return;
    $('html, body').removeClass('superga-care-scroll-lock');
    document.body.style.top = '';
    window.scrollTo(0, supergaCareScrollY);
  }

  function closeSupergaCare() {
    if (typeof window.destroySupergaCareScroll === 'function') {
      window.destroySupergaCareScroll();
    }
    $('.superga-care-root').removeClass('is-open');
    $('.superga-care').removeClass('show-overlay');
    unlockSupergaCarePageScroll();
    $('.js-superga-care-trigger').attr('aria-expanded', 'false');
  }

  function isSupergaCareOpen() {
    return $('.superga-care').hasClass('show-overlay');
  }

  function openSupergaCare() {
    var api = window.chHeaderModals;
    if (api && api.closeOthers) {
      api.closeOthers('superga-care');
    }

    lockSupergaCarePageScroll();
    var root = getActiveSupergaCareRoot();
    if (!root) return;

    $(root).addClass('is-open');
    $(root).find('.superga-care').addClass('show-overlay');
    $('.js-superga-care-trigger').attr('aria-expanded', 'true');

    if (root.classList.contains('superga-care-root--mobile')) return;

    window.setTimeout(function () {
      if (typeof window.initSupergaCareScroll !== 'function') return;
      window.initSupergaCareScroll(root);
    }, 400);
  }

  window.chHeaderModals.closeSupergaCare = closeSupergaCare;
  window.chHeaderModals.isSupergaCareOpen = isSupergaCareOpen;

  function bindSupergaCareEvents() {
    if (!hasSupergaCare) return;

    $(document).on('click', '.js-superga-care-trigger, a[href="#supergaclean"]', function (e) {
      e.preventDefault();
      openSupergaCare();
    });

    $(document).on('click', '.superga-care__close, .superga-care-overlay', function (e) {
      e.preventDefault();
      closeSupergaCare();
    });

    $(document).on('keydown', function (e) {
      if (e.key === 'Escape' && isSupergaCareOpen()) {
        closeSupergaCare();
      }
    });
  }

  /* =============================================================================
   * BAGIAN 4: Account overview customer data
   * ============================================================================= */
  var ACCOUNT_CUSTOMER_API_BASE = 'https://atmos-custom-plugin-front.devbdd.com/api/customers';

  // Ambil domain Shopify aktif dari form, fallback ke Shopify global.
  function getAccountShopifyDomain($form) {
    return (
      $form.find('[name="url_shopify"]').val() ||
      (window.Shopify && window.Shopify.shop) ||
      ''
    );
  }

  // Sisakan hanya digit dari value apa pun.
  function getAccountDigits(value) {
    return String(value || '').replace(/\D/g, '');
  }

  // Format nomor untuk ditampilkan di input, tanpa prefix +62.
  function getAccountPhoneDisplayValue(value) {
    var phone = getAccountDigits(value);

    if (phone.indexOf('62') === 0) phone = phone.slice(2);
    if (phone.indexOf('0') === 0) phone = phone.slice(1);
    return phone;
  }

  // Format nomor untuk payload API, selalu pakai prefix +62.
  function getAccountPhonePayloadValue(value) {
    var phone = getAccountPhoneDisplayValue(value);
    return phone ? '+62 ' + phone : '';
  }

  // Normalisasi DOB dari API/note supaya flatpickr selalu dapat format d/m/Y yang valid.
  function normalizeAccountDatebirthValue(value) {
    var raw = String(value || '').trim();
    var dateMatch;

    if (!raw || raw === '--') return '';

    dateMatch = raw.match(/(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})/);
    if (dateMatch) {
      return dateMatch[1] + '/' + dateMatch[2] + '/' + dateMatch[3];
    }

    dateMatch = raw.match(/(\d{4})[\/.-](\d{1,2})[\/.-](\d{1,2})/);
    if (dateMatch) {
      return dateMatch[3] + '/' + dateMatch[2] + '/' + dateMatch[1];
    }

    return '';
  }

  // Ambil DOB dari field terpisah day/month/year seperti struktur API lama.
  function getAccountDatebirthFromParts(source) {
    var day = source && (source.day || source.birth_day || source.date_day);
    var month = source && (source.month || source.birth_month || source.date_month);
    var year = source && (source.year || source.birth_year || source.date_year);

    if (!day || !month || !year) return '';

    day = String(day).padStart(2, '0');
    month = String(month).padStart(2, '0');
    year = String(year);

    return normalizeAccountDatebirthValue(day + '/' + month + '/' + year);
  }

  // Pecah DOB d/m/Y menjadi object day/month/year untuk payload API.
  function getAccountDatebirthParts(value) {
    var datebirth = normalizeAccountDatebirthValue(value);
    var parts = datebirth ? datebirth.split('/') : [];

    return {
      day: parts[0] || '',
      month: parts[1] || '',
      year: parts[2] || '',
    };
  }

  // Sync hidden input DOB terpisah tanpa mengubah UI single field.
  function syncAccountDatebirthParts(value) {
    var parts = getAccountDatebirthParts(value);

    $('#PersonalForm-Datebirth-Day').val(parts.day);
    $('#PersonalForm-Datebirth-Month').val(parts.month);
    $('#PersonalForm-Datebirth-Year').val(parts.year);

    return parts;
  }

  // Ambil birthday dari response API, termasuk fallback dari note customer.
  function getAccountDatebirthValue(response) {
    var data = response && response.data;
    var value = response && (response.bod || response.datebirth || response.birthday || response.dob);
    var note = response && (response.note || (response.data && response.data.note));
    var match;

    if (!value) {
      value = getAccountDatebirthFromParts(response);
    }

    if (!value && data) {
      value = data.bod || data.datebirth || data.birthday || data.dob || getAccountDatebirthFromParts(data);
    }

    if (!value && note) {
      match = String(note).match(/birthday:\s*([^|]*?)(?=\s+(?:gender|phone|shoes)\s*:|$)/i);
      value = match ? match[1].trim() : '';
    }

    return normalizeAccountDatebirthValue(value);
  }

  // Buat note customer eksplisit supaya birthday tidak berubah jadi "--" di API/plugin.
  function buildAccountCustomerNote(payload) {
    var gender = payload.gender || $('#PersonalForm-Gender').val() || '';
    var birthday = payload.datebirth || $('#PersonalForm-Datebirth').val() || '';
    var phone = payload.phone || getAccountPhonePayloadValue($('#PersonalForm-Phone').val());
    var genderLabel = gender ? gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase() : '';

    return [
      'Gender: ' + genderLabel,
      'birthday: ' + (birthday || '--'),
      'Phone: ' + (phone || ''),
      'Shoes Size (US): ',
    ].join('\n');
  }

  // Ubah form jQuery menjadi object JSON yang siap dikirim ke API.
  function serializeFormToObject($form) {
    var payload = {};
    var formDataArray = $form.serializeArray();

    $.each(formDataArray, function () {
      payload[this.name] = this.value;
    });

    if (Object.prototype.hasOwnProperty.call(payload, 'phone')) {
      payload.phone = getAccountPhonePayloadValue(payload.phone);
    }

    payload.datebirth = normalizeAccountDatebirthValue(payload.datebirth) || getAccountDatebirthFromParts(payload);

    if (payload.datebirth) {
      var datebirthParts = getAccountDatebirthParts(payload.datebirth);

      payload.day = datebirthParts.day;
      payload.month = datebirthParts.month;
      payload.year = datebirthParts.year;
      payload.bod = payload.datebirth;
      payload.dob = payload.datebirth;
      payload.birthday = payload.datebirth;
      syncAccountDatebirthParts(payload.datebirth);
    }

    payload.note = buildAccountCustomerNote(payload);

    if (!payload.email && $('#PersonalForm-Email').length) {
      payload.email = $('#PersonalForm-Email').val();
    }

    return payload;
  }

  // Init flatpickr untuk field date of birth jika library tersedia.
  function initPersonalDatebirthPicker() {
    var $el = $('[id="PersonalForm-Datebirth"]');
    if (!$el.length) return null;
    if ($el.data('flatpickrReady')) return $el[0]._flatpickr || null;
    if (typeof window.flatpickr !== 'function') return null;

    $el.data('flatpickrReady', true);
    return window.flatpickr('[id="PersonalForm-Datebirth"]', {
      dateFormat: 'd/m/Y',
      altInput: false,
      disableMobile: true,
      onChange: function (selectedDates, dateStr) {
        syncAccountDatebirthParts(dateStr);
      },
    });
  }

  // Tunggu flatpickr karena script CDN di account page bisa load setelah app-js.
  function waitForPersonalDatebirthPicker(callback, attempt) {
    var datebirthPicker = initPersonalDatebirthPicker();
    var nextAttempt = attempt || 0;

    if (datebirthPicker || nextAttempt >= 40) {
      if ($.isFunction(callback)) callback(datebirthPicker);
      return;
    }

    window.setTimeout(function () {
      waitForPersonalDatebirthPicker(callback, nextAttempt + 1);
    }, 100);
  }

  // Kirim update customer ke custom plugin API.
  function updateCustomerData(payload) {
    return $.ajax({
      url: ACCOUNT_CUSTOMER_API_BASE + '/update_data',
      type: 'POST',
      data: JSON.stringify(payload),
      contentType: 'application/json',
      dataType: 'json',
      error: function (xhr) {
        alert('Submission failed! Please try again.');
        console.error(xhr.responseText);
      },
    });
  }

  // Submit form personal data dengan feedback tombol agar user tahu request jalan.
  function submitAccountOverviewData($form) {
    var $button = $form.find('.btn-customer-personal');
    var defaultText = $button.text();
    var payload = serializeFormToObject($form);

    $button.prop('disabled', true).text('UPDATING...');
    console.log('Account data payload:', payload);

    updateCustomerData(payload)
      .done(function (response) {
        console.log('Account data updated:', response);
        if (payload.datebirth) {
          $('[id="PersonalForm-Datebirth"]').val(payload.datebirth);
        }
        $button.text('UPDATED');
      })
      .fail(function () {
        $button.text('UPDATE FAILED');
      })
      .always(function () {
        window.setTimeout(function () {
          $button.prop('disabled', false).text(defaultText);
        }, 1200);
      });
  }

  // Bind fetch awal dan submit handler untuk form account overview.
  function bindAccountOverviewForms() {
    var $personalForm = $('.personal-information-form');
    if (!$personalForm.length || $personalForm.data('accountOverviewBound')) return;

    $personalForm.data('accountOverviewBound', true);

    var datebirthPicker = null;
    var customerId = $personalForm.attr('data-id');
    var requestData = {
      id: customerId,
      url_shopify: getAccountShopifyDomain($personalForm),
    };

    waitForPersonalDatebirthPicker(function (picker) {
      datebirthPicker = picker;

      var initialDateVal = $('[id="PersonalForm-Datebirth"]').val();
      if (initialDateVal && datebirthPicker) {
        if (typeof datebirthPicker.setDate === 'function') {
          datebirthPicker.setDate(initialDateVal, false, 'd/m/Y');
        }
        syncAccountDatebirthParts(initialDateVal);
      }
    });

    $.ajax({
      type: 'POST',
      url: ACCOUNT_CUSTOMER_API_BASE + '/get_data',
      data: requestData,
      dataType: 'json',
      success: function (response) {
        var datebirth = getAccountDatebirthValue(response);
        var responsePhone = response.phone || (response.data && response.data.phone);

        if (responsePhone) {
          var displayPhone = getAccountPhoneDisplayValue(responsePhone);
          $('[id="PersonalForm-Phone"]').val(displayPhone);
          $('[id="PhoneForm-Phone"]').val(getAccountPhonePayloadValue(displayPhone));
        }

        if (datebirth) {
          if (datebirthPicker && typeof datebirthPicker.setDate === 'function') {
            datebirthPicker.setDate(datebirth, true, 'd/m/Y');
          } else {
            $('[id="PersonalForm-Datebirth"]').val(datebirth);
          }
          syncAccountDatebirthParts(datebirth);
        }

        if (response.gender) {
          $('[id="PersonalForm-Gender"]').val(response.gender.toLowerCase());
          $('[id="PhoneForm-Gender"]').val(response.gender.toLowerCase());
        }
      },
      error: function (response) {
        console.log(response);
      },
    });

    $personalForm.on('submit', function (event) {
      event.preventDefault();
      submitAccountOverviewData($(this));
    });

    $(document).on('input', '[id="PersonalForm-Phone"]', function () {
      this.value = getAccountPhoneDisplayValue(this.value);
    });

    $(document).on('click', '.btn-customer-personal', function (event) {
      event.preventDefault();
      $(this).closest('form').trigger('submit');
    });

    $(document).on('click', '[id="phone-number-btn"]', function () {
      var $btn = $(this);
      var $form = $btn.closest('form');
      var $phoneForm = $btn.closest('.personal-information-form').siblings('[id="phone-number-form"]');
      if (!$phoneForm.length) {
        $phoneForm = $('[id="phone-number-form"]');
      }
      var phone = getAccountPhonePayloadValue($form.find('[id="PersonalForm-Phone"]').val());
      var datebirth = $form.find('[id="PersonalForm-Datebirth"]').val();
      var gender = $form.find('[id="PersonalForm-Gender"]').val();
      var payload = {
        id: $phoneForm.attr('data-id'),
        url_shopify: getAccountShopifyDomain($phoneForm),
        phone: phone,
      };

      $('[id="PhoneForm-Phone"]').val(phone);

      if (datebirth) {
        var datebirthParts = getAccountDatebirthParts(datebirth);

        payload.datebirth = datebirth;
        payload.day = datebirthParts.day;
        payload.month = datebirthParts.month;
        payload.year = datebirthParts.year;
        payload.bod = datebirth;
        payload.dob = datebirth;
        payload.birthday = datebirth;
        syncAccountDatebirthParts(datebirth);
      }

      if (gender) {
        payload.gender = gender;
      }

      payload.note = buildAccountCustomerNote(payload);

      updateCustomerData(payload);
    });

    $(document).on('submit', '[id="phone-number-form"]', function (event) {
      event.preventDefault();
      updateCustomerData(serializeFormToObject($(this)));
    });
  }

  // Bind handler layout address lama jika markup custom address tersedia.
  function bindAccountAddressLayoutEvents() {
    if (!$('.customer.addresses').length || $(document).data('accountAddressLayoutBound')) return;

    $(document).data('accountAddressLayoutBound', true);

    $(document).on('click', '.btn-add-new-address', function () {
      $('#address-sidebar-add').addClass('is-open');
      $('body').addClass('address-scroll-lock');
    });

    $(document).on('click', '.address-sidebar-overlay, .address-sidebar-close', function () {
      $('.address-sidebar-root').removeClass('is-open');
      $('body').removeClass('address-scroll-lock');

      $('.customer-address-form-add input').not('[name="address[country]"], [name="address[province]"], [name="form_type"], [name="utf8"]').val('');
      $('.customer-address-form-add input[type="checkbox"]').prop('checked', false).removeAttr('checked');
    });

    $(document).on('click', '.customer-address-form .btn-delete', function () {
      $('.address-sidebar-root').removeClass('is-open');
      $('body').removeClass('address-scroll-lock');

      $('.customer-address-form-add input').not('[name="address[country]"], [name="address[province]"], [name="form_type"], [name="utf8"]').val('');
      $('.customer-address-form-add input[type="checkbox"]').prop('checked', false).removeAttr('checked');
    });

    $(document).on('click', '.btn-address-edit', function () {
      var id = $(this).attr('data-id');
      $('#address-sidebar-edit').addClass('is-open');
      $('.customer-address-form-edit').hide();
      $('#address_form_' + id).show();
      $('body').addClass('address-scroll-lock');
    });
  }

  // wishlist
  if (window.location.href.includes('a/wishlist')) {
    // Tandai body sebagai page wishlist supaya selector CSS/JS lebih spesifik.
    $('body').addClass('template-wishlist')
    $('body').toggleClass('template-wishlist-customer', !!window.isCustomerLoggedIn)
    $('body').toggleClass('template-wishlist-guest', !window.isCustomerLoggedIn)

    // Inject section custom setelah markup bawaan app wishlist.
    $('.wishlistpage').after('<div class="wishlist-page" id="wishlist-page"></div>')
    // Load Shopify section yang berisi header, button empty state, dan container product.
    $(document).find('.wishlist-page').load("/?section_id=wishlist-section", function () {
      // Login atau guest sama-sama pakai hasil render/storage dari Smart Wishlist app.
      renderWishlistFromSmartWishlistApp()
    })
  }



  /** Format harga dari response Smart Wishlist/Product JSON ke angka rupiah. */
  function formatWishlistAppRupiah(value) {
    let amount = parseFloat(String(value || '').replace(/[^\d.]/g, ''));

    if (isNaN(amount)) return '';

    return Math.round(amount).toLocaleString('id-ID');
  }

  const WISHLIST_SWATCH_API_URL = 'https://on-running.devbdd.com/config/getProductSame_db';
  const wishlistSameProductCache = {};
  const wishlistSameProductPending = {};

  /** Ambil handle product Shopify dari URL produk wishlist. */
  function getWishlistProductHandleFromUrl(url) {
    let match = String(url || '').match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : '';
  }

  /** Buat HTML satu item color swatch untuk card wishlist. */
  function buildWishlistSwatchSlide(item, isCurrent) {
    let activeClass = isCurrent ? ' active' : '';
    let handle = item && item.handle ? item.handle : '';
    let image = item && item.image ? item.image : '';

    if (image && typeof image === 'object') {
      image = image.src || '';
    }

    return (
      '<div class="color-swatch-item swiper-slide' +
      activeClass +
      '" data-handle="' +
      escapeHtml(handle) +
      '" data-url="/products/' +
      escapeHtml(handle) +
      '">' +
      '<button type="button" class="result-swatch-item" aria-label="Select color" aria-pressed="' +
      (isCurrent ? 'true' : 'false') +
      '">' +
      '<div class="swatch-img">' +
      '<img src="' +
      escapeHtml(image) +
      '" alt="" loading="lazy" width="34" height="24">' +
      '</div>' +
      '</button>' +
      '</div>'
    );
  }

  /** Fetch daftar produk warna lain untuk wishlist dengan cache per handle. */
  function fetchWishlistSameProducts(productHandle, payload) {
    if (wishlistSameProductCache[productHandle]) {
      return Promise.resolve(wishlistSameProductCache[productHandle]);
    }

    if (wishlistSameProductPending[productHandle]) {
      return wishlistSameProductPending[productHandle];
    }

    wishlistSameProductPending[productHandle] = $.ajax({
      type: 'POST',
      url: WISHLIST_SWATCH_API_URL,
      data: payload,
      crossDomain: true,
    }).then(function (response) {
      let data = typeof response === 'string' ? JSON.parse(response) : response;
      wishlistSameProductCache[productHandle] = data;
      delete wishlistSameProductPending[productHandle];
      return data;
    }).catch(function (err) {
      delete wishlistSameProductPending[productHandle];
      throw err;
    });

    return wishlistSameProductPending[productHandle];
  }

  /** Inisialisasi Swiper untuk color swatch wishlist jika fallback dipakai. */
  function initWishlistSwatchSwiper($root) {
    let el = $root.closest('.color-swatch-swiper')[0];
    if (!el || el.dataset.swatchSwiperInit === 'true') return;

    if (typeof Swiper === 'undefined') return;

    new Swiper(el, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: { enabled: true, sticky: false },
      nested: true,
      touchReleaseOnEdges: true,
      watchOverflow: true,
      grabCursor: true,
      resistanceRatio: 0,
    });

    el.dataset.swatchSwiperInit = 'true';
  }

  /** Terapkan data produk baru ke card wishlist saat swatch berubah. */
  function applyWishlistProductToCard($product, product) {
    let images = product && product.images ? product.images : [];
    let primaryImage = images[0] || product.featured_image || '';
    let secondaryImage = images[1] || '';
    let url = product.url || ('/products/' + product.handle);
    let $imageLink = $product.find('.product-card-2026__image-link').first();
    let $primary = $product.find('.product-card-2026__image--primary').first();
    let $secondary = $product.find('.product-card-2026__image--secondary').first();

    $imageLink.attr('href', url);

    if (primaryImage) {
      if (!$primary.length) {
        $primary = $('<img class="product-card-2026__image product-card-2026__image--primary" loading="lazy">');
        $imageLink.empty().append($primary);
      }

      $primary.attr('src', primaryImage).attr('alt', product.title || '');
    }

    if (secondaryImage) {
      if (!$secondary.length) {
        $secondary = $('<img class="product-card-2026__image product-card-2026__image--secondary" loading="lazy">');
        $imageLink.append($secondary);
      }

      $secondary.attr('src', secondaryImage).attr('alt', product.title || '');
    } else {
      $secondary.remove();
    }

    $product.find('.product-card-2026__title').attr('href', url).text(product.title || '');
    $product.find('.product-card-2026__price').text(formatWishlistProductCardPrice(product.price || 0));
    $product.find('.product-card-2026__info--sizes').html(buildWishlistSizeMarkup(product));
  }

  /** Pasang handler klik swatch wishlist agar card berubah warna/produk. */
  function bindWishlistSwatchClicks($root) {
    if ($root.data('wishlistSwatchClicksBound')) return;
    $root.data('wishlistSwatchClicksBound', true);

    $root.on('click', '.result-swatch-item', function (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      let $button = $(this);
      let $item = $button.closest('.color-swatch-item');
      let handle = $item.data('handle');
      let $product = $root.closest('.wishlist-app-product-card');

      if (!handle || !$product.length) return;

      if ($item.hasClass('active')) {
        window.location.href = '/products/' + handle;
        return;
      }

      $root.find('.color-swatch-item').removeClass('active');
      $root.find('.result-swatch-item').attr('aria-pressed', 'false');
      $item.addClass('active');
      $button.attr('aria-pressed', 'true');

      let previewImage = $item.find('.swatch-img img').attr('src');
      if (previewImage) {
        $product.find('.product-card-2026__image--primary').first().attr('src', previewImage);
      }

      getProductData(handle)
        .then(function (product) {
          applyWishlistProductToCard($product, product);
        })
        .catch(function () { });
    });
  }

  /** Update disabled state tombol prev/next swatch wishlist. */
  function updateWishlistSwatchNavigationState(el) {
    if (!el || !el.swiper) return;

    let prevButton = el.querySelector('.color-swatch-nav--prev');
    let nextButton = el.querySelector('.color-swatch-nav--next');

    if (!prevButton || !nextButton) return;

    prevButton.disabled = el.swiper.isBeginning;
    nextButton.disabled = el.swiper.isEnd;
  }

  /** Pastikan tombol prev/next swatch wishlist tersedia saat item warna banyak. */
  function ensureWishlistSwatchNavigation($root) {
    let el = $root.closest('.color-swatch-swiper')[0];

    if (!el) return;

    let swatchCount = $root.children('.color-swatch-item:not(.draft)').length;

    if (swatchCount <= 5) {
      el.classList.remove('has-swatch-nav');
      el.querySelectorAll('.color-swatch-nav').forEach(function (button) {
        button.remove();
      });
      return;
    }

    el.classList.add('has-swatch-nav');

    if (!el.querySelector('.color-swatch-nav--prev')) {
      el.insertAdjacentHTML(
        'beforeend',
        '<button type="button" class="color-swatch-nav color-swatch-nav--prev" aria-label="Previous color swatches">' +
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="m4.707 12.5 4.147 4.146-.707.708L2.793 12l5.354-5.354.707.708L4.707 11.5H21v1H4.707Z"></path>' +
        '</svg>' +
        '</button>' +
        '<button type="button" class="color-swatch-nav color-swatch-nav--next" aria-label="Next color swatches">' +
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="m19.293 11.5-4.147-4.146.708-.708L21.207 12l-5.353 5.354-.708-.707 4.147-4.147H3v-1h16.293Z"></path>' +
        '</svg>' +
        '</button>'
      );
    }

    if (el.dataset.wishlistSwatchNavBound !== 'true') {
      el.dataset.wishlistSwatchNavBound = 'true';

      el.addEventListener('click', function (event) {
        let button = event.target.closest('.color-swatch-nav');

        if (!button || !el.swiper) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (button.classList.contains('color-swatch-nav--prev')) {
          el.swiper.slidePrev();
        } else {
          el.swiper.slideNext();
        }

        button.blur();
        updateWishlistSwatchNavigationState(el);
      });
    }

    updateWishlistSwatchNavigationState(el);

    if (el.swiper && el.dataset.wishlistSwatchNavEventsBound !== 'true') {
      el.dataset.wishlistSwatchNavEventsBound = 'true';
      el.swiper.on('slideChange update resize', function () {
        updateWishlistSwatchNavigationState(el);
      });
    }
  }

  /** Retry pemasangan tombol nav swatch setelah shared swatch selesai render. */
  function scheduleWishlistSwatchNavigation($root) {
    let attempts = 0;
    let interval = setInterval(function () {
      attempts += 1;

      if ($root.children('.color-swatch-item:not(.draft)').length) {
        ensureWishlistSwatchNavigation($root);
      }

      if (attempts > 20 || $root.closest('.color-swatch-swiper').find('.color-swatch-nav').length) {
        clearInterval(interval);
      }
    }, 100);
  }

  /** Render color swatch ke product card wishlist dari shared swatch atau fallback. */
  function renderWishlistColorSwatches($product, product, fallbackHandle) {
    let productHandle = product && product.handle ? product.handle : fallbackHandle;
    let productTitle = product && product.title ? product.title : '';

    if (!$product.length || !productHandle || $product.data('wishlistSwatchesLoaded')) return;

    $product.data('wishlistSwatchesLoaded', true);

    let $root = $product.find('.product-card-2026__variant-thumbs .color-swatches').first();

    if (!$root.length) {
      $product.find('.wishlist-app-product-card__image-wrap').after(
        '<div class="product-card-2026__variant-thumbs color-swatch-swiper swiper" aria-hidden="true">' +
        '<div class="color-swatches swiper-wrapper desktop-only"></div>' +
        '</div>'
      );
      $root = $product.find('.product-card-2026__variant-thumbs .color-swatches').first();
    }

    $root.html(
      '<input type="hidden" class="color-swatch-input color-swatch-product-name" value="' +
      escapeHtml(productTitle) +
      '">' +
      '<input type="hidden" class="color-swatch-input color-swatch-product-handle" value="' +
      escapeHtml(productHandle) +
      '">' +
      '<input type="hidden" class="color-swatch-handle20" value="' +
      escapeHtml(productTitle.slice(0, Math.max(productTitle.length - Math.floor(productTitle.length * 0.05), 0))) +
      '">'
    );

    let payload = {
      product_title: String(productTitle).replace(/melissa /gi, ''),
      product_fulltitle: productTitle,
      handle: productHandle,
      shop: typeof Shopify !== 'undefined' ? Shopify.shop : '',
      only_color: true,
    };

    if (window.__ColorSwatchShared && $.isFunction(window.__ColorSwatchShared.queueCard)) {
      bindWishlistSwatchClicks($root);
      window.__ColorSwatchShared.queueCard($root, productTitle, productHandle);
      scheduleWishlistSwatchNavigation($root);

      fetchWishlistSameProducts(productHandle, payload)
        .then(function (data) {
          let dataSame = data && data.data_same;
          let products = Array.isArray(dataSame) ? dataSame : Object.values(dataSame || {});
          if (products.length > 0) {
            $product.find('.product-card-2026__mobile-color-count').remove();
            let colorCountText = products.length + ' Colour' + (products.length > 1 ? '[s]' : '');
            $product.find('.product-card-2026__title').after(
              '<div class="color-swatches product-card-2026__mobile-color-count mobile-only">' +
              '<span class="mobile-color-count-text">' + colorCountText + '</span>' +
              '</div>'
            );
          }
        }).catch(function () { });
      return;
    }

    fetchWishlistSameProducts(productHandle, payload)
      .then(function (data) {
        let dataSame = data && data.data_same;
        let products = Array.isArray(dataSame) ? dataSame : Object.values(dataSame || {});

        if (products.length > 0) {
          $product.find('.product-card-2026__mobile-color-count').remove();
          let colorCountText = products.length + ' Colour' + (products.length > 1 ? '[s]' : '');
          $product.find('.product-card-2026__title').after(
            '<div class="color-swatches product-card-2026__mobile-color-count mobile-only">' +
            '<span class="mobile-color-count-text">' + colorCountText + '</span>' +
            '</div>'
          );
        }

        let currentSlides = [];
        let otherSlides = [];

        products.forEach(function (item) {
          if (!item || item.status === 'draft' || !item.handle) return;

          let isCurrent = item.handle === productHandle;
          let html = buildWishlistSwatchSlide(item, isCurrent);

          if (isCurrent) {
            currentSlides.push(html);
          } else {
            otherSlides.push(html);
          }
        });

        if (!currentSlides.length && productHandle) {
          currentSlides.push(buildWishlistSwatchSlide({
            handle: productHandle,
            image: product && product.image ? product.image.src : product.featured_image,
          }, true));
        }

        $root.html(currentSlides.join('') + otherSlides.join(''));
        bindWishlistSwatchClicks($root);
        initWishlistSwatchSwiper($root);
        ensureWishlistSwatchNavigation($root);
      })
      .catch(function () { });
  }

  /** Ubah markup item Smart Wishlist menjadi product-card-2026 custom. */
  function decorateSmartWishlistItem($item) {
    let $product = $item.find('.product').first();
    let $imageLink = $product.find('.image a').first();
    let productUrl = ($imageLink.attr('href') || '').split('?')[0];
    let productHandle = getWishlistProductHandleFromUrl(productUrl);
    let productId = $product.attr('data-productid');
    let variantId = $product.attr('data-variantid');
    let productImage = $product.find('.buttons a.singleremovebutton').attr('data-product_image') || '';

    if ($item.data('smartWishlistDecorated')) return;
    $item.data('smartWishlistDecorated', true);
    $item.addClass('wishlist-app-item');
    $product.addClass('product-card-2026 wishlist-app-product-card');
    $product.find('.image').addClass('wishlist-app-product-card__image-wrap');
    $imageLink.addClass('product-card-2026__image-link');
    $imageLink.find('img').addClass('product-card-2026__image product-card-2026__image--primary');
    $product.find('.text').addClass('product-card-2026__info product-card-2026__info--default');
    $product.find('.text a, .text h3, .text .title').first().addClass('product-card-2026__title');

    if (!$product.find('.product-card-2026__info--sizes').length) {
      $product.find('.text').after('<div class="product-card-2026__info product-card-2026__info--sizes" aria-hidden="true"></div>');
    }

    // Harga bawaan app dibersihkan, lalu vendor/price diambil dari Shopify product JSON.
    $item.find('.product p .compare-price, .product p .price, .product p.price').remove();
    $item.find('.product-vendor, .product .text > .price, .product .text > .product-card-2026__price').remove();

    if (productUrl) {
      $.ajax({
        type: 'GET',
        url: productUrl + '.json',
        success: function (response) {
          let product = response && response.product;
          let firstVariant = product && product.variants && product.variants[0];
          let productVendor = product && product.vendor;
          let productPrice = firstVariant ? formatWishlistAppRupiah(firstVariant.price) : '';

          if (productVendor) {
            $product.find('.text').prepend('<div class="product-vendor">' + productVendor + '</div>');
          }

          if (productPrice) {
            $product.find('.text').append('<p class="price product-card-2026__price">IDR ' + productPrice + '</p>');
          }

          getProductData(productHandle)
            .then(function (productData) {
              applyWishlistProductToCard($product, productData);
              renderWishlistColorSwatches($product, productData, productHandle);
            })
            .catch(function () {
              renderWishlistColorSwatches($product, product, productHandle);
            });
        },
        error: function (xhr) {
          console.log(xhr.responseText);
        },
      });
    }

    // Tombol delete tetap pakai class/data bawaan Smart Wishlist supaya storage app ikut ke-update.
    if (!$product.find('.wishlist-btn-delete').length && productId && variantId) {
      $product.append(
        '<button type="button" class="wishlist-btn-delete removebutton singleremovebutton" aria-label="Remove from wishlist" data-product_image="' +
        productImage +
        '" data-productid="' +
        productId +
        '" data-variantid="' +
        variantId +
        '">' +
        '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>' +
        '</svg>' +
        '</button>'
      );
    }
  }

  /** Decorate semua item produk yang dirender app Smart Wishlist. */
  function decorateSmartWishlistProducts($bookmarks) {
    let $items = $bookmarks.find('.container-fluid .row .col-md-4');

    if (!$items.length) {
      $items = $bookmarks.find('.products .product').map(function () {
        return $(this).closest('.col-md-4').length ? $(this).closest('.col-md-4')[0] : this;
      });
    }

    $items.each(function () {
      decorateSmartWishlistItem($(this));
    });
  }

  /** Ubah modal remove bawaan Smart Wishlist menjadi desain custom. */
  function decorateWishlistRemoveModal($trigger) {
    let $modal = $('#remove_modal');
    let $content = $modal.find('.modal-content').first();

    if (!$modal.length || !$content.length || $content.data('wishlistRemoveStyled')) return;

    $content.data('wishlistRemoveStyled', true);
    $modal.addClass('wishlist-remove-modal');

    let $header = $content.find('.modal-header').first();
    let $body = $content.find('.modal-body').first();
    let $footer = $content.find('.modal-footer').first();
    let $close = $header.find('button.close').first();
    let $image = $body.find('#remove_modal_image').first();
    let $remove = $footer.find('#modal_remove_button').first();
    let $back = $footer.find('[data-dismiss="modal"]').first();
    let productTitle = ($trigger && $trigger.closest('.wishlist-app-product-card').find('.product-card-2026__title').first().text()) || '';

    $header.remove();
    $footer.remove();

    $close
      .attr('aria-label', 'Close')
      .html(
        '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>' +
        '</svg>'
      );

    $remove
      .text('Agree')
      .removeClass('btn btn-warning btn-warning-modal')
      .addClass('remove-button-agree');

    $back
      .text('Turning Back')
      .removeClass('btn btn-default btn-default-modal')
      .addClass('remove-button-back');

    $body.empty().append(
      $('<div class="wishlist-remove-modal__image"></div>').append($image),
      $('<div class="wishlist-remove-modal__content"></div>').append(
        $close,
        '<div class="wishlist-remove-modal__title">Remove from Wishlist?</div>',
        '<div class="wishlist-remove-modal__description">You\'ll lost this item forever.</div>',
        productTitle ? '<div class="wishlist-remove-modal__product-title">' + escapeHtml(productTitle) + '</div>' : '',
        $('<div class="wishlist-remove-modal__buttons"></div>').append($remove, $back)
      )
    );
  }

  /** Tunggu modal remove muncul, lalu decorate begitu markup tersedia. */
  function scheduleWishlistRemoveModal($trigger) {
    let attempts = 0;
    let interval = setInterval(function () {
      attempts += 1;

      if ($('#remove_modal .modal-content').length) {
        decorateWishlistRemoveModal($trigger);
        clearInterval(interval);
      }

      if (attempts > 50) {
        clearInterval(interval);
      }
    }, 50);
  }

  $(document).on('click', '.wishlist-btn-delete', function () {
    scheduleWishlistRemoveModal($(this));
  });

  $(document).on('click', '.remove-button-agree', function () {
    setTimeout(function () {
      updateWishlistCount();
    }, 500);
  });

  /** Update angka wishlist count badge di header */
  function updateWishlistCount(count) {
    var total = 0;
    if (typeof count === 'number' && !isNaN(count)) {
      total = count;
    } else if ($('#wishlist-section-products .wishlist-app-product-card, #wishlist-section-products .product-card-2026, #wishlist-section-products .col-md-4').length) {
      total = $('#wishlist-section-products .wishlist-app-product-card, #wishlist-section-products .product-card-2026, #wishlist-section-products .col-md-4').length;
    } else {
      var $appCount = $('#bookmarkcount, .bookmarkcount');
      if ($appCount.length && $appCount.first().text().trim() !== '') {
        var parsed = parseInt($appCount.first().text().replace(/[^\d]/g, ''), 10);
        if (!isNaN(parsed)) total = parsed;
      } else {
        try {
          var swData = localStorage.getItem('smartwishlist_products') || localStorage.getItem('sw_products');
          if (swData) {
            var items = JSON.parse(swData);
            if (Array.isArray(items)) total = items.length;
          }
        } catch (e) { }
      }
    }
    $('#wishlist-link-custom .wishlist-count-text').text('[' + total + ']');
  }

  // Update angka wishlist awal saat page diload
  updateWishlistCount();

  // Sync count dari topbadge (gunakan element pertama agar tidak menggabungkan string dari multiple elements)
  function syncTopbadgeCount() {
    var $topbadge = $('.topbadge').first();
    if ($topbadge.length) {
      var val = $topbadge.text().trim();
      var parsed = parseInt(val.replace(/[^\d]/g, ''), 10);
      if (!isNaN(parsed)) {
        updateWishlistCount(parsed);
        return;
      }
    }
    updateWishlistCount();
  }

  var topbadgeInterval = setInterval(function () {
    syncTopbadgeCount();
  }, 500);

  $(document).on('click', '.smartwishlist, #smartwishlist, .sw_button, .wishlist-btn-delete, [id*="wishlist"]', function () {
    setTimeout(syncTopbadgeCount, 300);
    setTimeout(syncTopbadgeCount, 1000);
  });

  /** Tampilkan state kosong/tombol belanja saat wishlist tidak punya item. */
  function renderWishlistEmpty() {
    $('body').addClass('is-wishlist-empty');
    $('.wishlist-description').show();
    $('.wishlist-button').show();
  }

  /** Sembunyikan pesan kosong saat wishlist memiliki produk. */
  function renderWishlistFilled() {
    $('body').removeClass('is-wishlist-empty');
    $('.wishlist-description').hide();
    $('.wishlist-button').hide();
  }

  /** Ambil hasil render Smart Wishlist app dan pindahkan ke section custom. */
  function renderWishlistFromSmartWishlistApp() {
    $('#wishlist-section-products').empty();

    let wishlistAttempts = 0;
    let wishlistInterval = setInterval(() => {
      let $bookmarks = $('body.template-wishlist .wishlistpage .row.products #bookmarks');

      wishlistAttempts += 1;

      if (!$('#wishlist-section').length || !$bookmarks.length) {
        if (wishlistAttempts > 100) {
          renderWishlistEmpty();
          updateWishlistCount(0);
          clearInterval(wishlistInterval);
        }
        return;
      }

      if ($bookmarks.find('.empty-wishlist').length) {
        $bookmarks.find('.empty-wishlist').remove();
        renderWishlistEmpty();
        updateWishlistCount(0);
        clearInterval(wishlistInterval);
        return;
      }

      if ($bookmarks.find('.container-fluid, .products .product, a[href*="/products/"]').length) {
        renderWishlistFilled();
        decorateSmartWishlistProducts($bookmarks);
        $('#wishlist-section-products').empty().append($bookmarks);
        $('body').addClass('template-wishlist-app-rendered');
        updateWishlistCount();
        clearInterval(wishlistInterval);
      }
    }, 50);
  }

  /** Sidebar menu accordion (support level 3+) */
  function bindSidebarMenuAccordion() {
    const plusIcon = `
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.5 0.5V12.5M12.5 6.5H0.5"
            stroke="#383838"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"/>
    </svg>
  `;

    const minusIcon = `
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.5 6.5H12.5"
            stroke="#383838"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"/>
    </svg>
  `;

    function updateParentHeights(item) {
      let parent = item.parentElement.closest('.custom-sidebar-menu__item');

      while (parent) {
        if (parent.classList.contains('is-open')) {
          const parentSubmenu = parent.querySelector(':scope > .custom-sidebar-menu__submenu');

          if (parentSubmenu) {
            // Reset max-height dulu biar scrollHeight akurat
            parentSubmenu.style.maxHeight = 'none';
            const newHeight = parentSubmenu.scrollHeight;
            parentSubmenu.style.maxHeight = newHeight + 'px';
          }
        }

        parent = parent.parentElement.closest('.custom-sidebar-menu__item');
      }
    }

    $(document)
      .off('click.sidebarMenuAccordion')
      .on('click.sidebarMenuAccordion', '.custom-sidebar-menu__header', function (e) {

        const item = this.closest('.custom-sidebar-menu__item');
        const submenu = item.querySelector(':scope > .custom-sidebar-menu__submenu');

        if (!submenu) return;

        e.preventDefault();

        const toggle = this.querySelector('.custom-sidebar-menu__toggle');

        if (item.classList.contains('is-open')) {
          // Close: animasi close
          submenu.style.maxHeight = submenu.scrollHeight + 'px';

          requestAnimationFrame(() => {
            item.classList.remove('is-open');
            submenu.style.maxHeight = '0px';

            updateParentHeights(item);
          });

          if (toggle) {
            toggle.innerHTML = plusIcon;
          }

        } else {
          // Open: tambahkan class dulu
          item.classList.add('is-open');

          // Reset max-height ke none biar scrollHeight akurat
          submenu.style.maxHeight = 'none';
          const newHeight = submenu.scrollHeight;
          submenu.style.maxHeight = newHeight + 'px';

          // Update parent heights setelah submenu terbuka
          updateParentHeights(item);

          if (toggle) {
            toggle.innerHTML = minusIcon;
          }
        }
      });

    // Tambahkan event listener untuk klik pada submenu items yang memiliki submenu sendiri
    $(document).on('click.sidebarMenuAccordion', '.custom-sidebar-menu__item .custom-sidebar-menu__item .custom-sidebar-menu__header', function (e) {
      // Biarkan event handler utama menangani, tapi setelah itu update parents
      setTimeout(() => {
        const item = this.closest('.custom-sidebar-menu__item');
        updateParentHeights(item);
      }, 50);
    });
  }

  /* Init bagian 2: pasang event + sync cart awal */
  bindCartEvents();
  syncCartUI();

  /* --- Account Mobile --- */

  /*
  $(document).on('click', '[data-open-account-mobile="true"]', function (e) {
    e.preventDefault();
    if (window.chHeaderModals && window.chHeaderModals.closeOthers) {
      window.chHeaderModals.closeOthers('account');
    }
    $('#AccountMenuMobile').addClass('is-open');
    $('html, body').addClass('account-scroll-lock overflow-hidden');
  });

  $(document).on('click', '#AccountMenuMobile .account-menu-mobile__close, #AccountMenuMobile .modal-overlay', function (e) {
    e.preventDefault();
    $('#AccountMenuMobile').removeClass('is-open');
    setTimeout(function () {
      if (!$('#AccountMenuMobile').hasClass('is-open') && !$('#AccountOverviewMobile').hasClass('is-open') && !$('#AccountAddressesMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  $(document).on('click', '#OpenAccountOverview', function (e) {
    e.preventDefault();
    $('#AccountMenuMobile').removeClass('is-open');
    $('#AccountOverviewMobile').addClass('is-open');
  });

  $(document).on('click', '#AccountOverviewMobile .account-overview-mobile__close, #AccountOverviewMobile .modal-overlay', function (e) {
    e.preventDefault();
    $('#AccountOverviewMobile').removeClass('is-open');
    setTimeout(function () {
      if (!$('#AccountMenuMobile').hasClass('is-open') && !$('#AccountOverviewMobile').hasClass('is-open') && !$('#AccountAddressesMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  $(document).on('click', '.account-overview-mobile__back', function (e) {
    e.preventDefault();
    $('#AccountOverviewMobile').removeClass('is-open');
    $('#AccountMenuMobile').addClass('is-open');
  });

  // Saved Address Mobile Popup JS
  $(document).on('click', '#OpenAccountAddresses', function (e) {
    e.preventDefault();
    $('#AccountMenuMobile').removeClass('is-open');
    $('#AccountAddressesMobile').addClass('is-open');
  });

  $(document).on('click', '#AccountAddressesMobile .account-overview-mobile__close, #AccountAddressesMobile .modal-overlay', function (e) {
    e.preventDefault();
    $('#AccountAddressesMobile').removeClass('is-open');
    setTimeout(function () {
      if ($('.account-overview-mobile-popup.is-open').length === 0 && !$('#AccountMenuMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  $(document).on('click', '#BackToAccountMenu', function (e) {
    e.preventDefault();
    $('#AccountAddressesMobile').removeClass('is-open');
    $('#AccountMenuMobile').addClass('is-open');
  });

  // Purchases Mobile Popup JS
  $(document).on('click', '#OpenAccountPurchasesMobile', function (e) {
    e.preventDefault();
    $('#AccountMenuMobile').removeClass('is-open');
    $('#AccountPurchasesMobile').addClass('is-open');
  });

  $(document).on('click', '#AccountPurchasesMobile .account-overview-mobile__close, #AccountPurchasesMobile .modal-overlay', function (e) {
    e.preventDefault();
    $('#AccountPurchasesMobile').removeClass('is-open');
    setTimeout(function () {
      if ($('.account-overview-mobile-popup.is-open').length === 0 && !$('#AccountMenuMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  $(document).on('click', '#BackToAccountMenuFromPurchases', function (e) {
    e.preventDefault();
    $('#AccountPurchasesMobile').removeClass('is-open');
    $('#AccountMenuMobile').addClass('is-open');
  });

  $(document).on('click', '#AccountPurchasesMobile .purchase-order-header', function (e) {
    e.preventDefault();
    var $content = $(this).next('.purchase-order-content');
    var $icon = $(this).find('svg');
    if ($content.is(':visible')) {
      $content.slideUp(200);
      $icon.css('transform', 'rotate(180deg)');
    } else {
      $content.slideDown(200);
      $icon.css('transform', 'rotate(0deg)');
    }
  });

  // Open Edit Address Popup
  $(document).on('click', '.account-address-card__edit', function (e) {
    e.preventDefault();
    var addressId = $(this).attr('data-address-id');
    $('#AccountAddressesMobile').removeClass('is-open');
    $('#AccountAddressEditMobile-' + addressId).addClass('is-open');
  });

  // Open Add Address Popup
  $(document).on('click', '#AddNewAddressMobileBtn', function (e) {
    e.preventDefault();
    $('#AccountAddressesMobile').removeClass('is-open');
    $('#AccountAddressAddMobile').addClass('is-open');
  });

  // Back from Edit/Add to Addresses List
  $(document).on('click', '.back-to-addresses-list', function (e) {
    e.preventDefault();
    $(this).closest('.account-overview-mobile-popup').removeClass('is-open');
    $('#AccountAddressesMobile').addClass('is-open');
  });

  // Close Edit/Add Popups
  $(document).on('click', '[id^="AccountAddressEditMobile-"] .account-overview-mobile__close, [id^="AccountAddressEditMobile-"] .modal-overlay, #AccountAddressAddMobile .account-overview-mobile__close, #AccountAddressAddMobile .modal-overlay', function (e) {
    e.preventDefault();
    $(this).closest('.account-overview-mobile-popup').removeClass('is-open');
    setTimeout(function () {
      if ($('.account-overview-mobile-popup.is-open').length === 0 && !$('#AccountMenuMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  // Reusable custom confirm modal function
  function showCustomConfirm(message, onConfirm) {
    var $modal = $('#customConfirmModal');
    if (!$modal.length) {
      var html = 
        '<div class="custom-confirm-modal" id="customConfirmModal" style="display:none;">' +
          '<div class="custom-confirm-modal__overlay"></div>' +
          '<div class="custom-confirm-modal__content">' +
            '<p class="custom-confirm-modal__message"></p>' +
            '<div class="custom-confirm-modal__buttons">' +
              '<button type="button" class="custom-confirm-modal__btn custom-confirm-modal__btn--cancel">CANCEL</button>' +
              '<button type="button" class="custom-confirm-modal__btn custom-confirm-modal__btn--confirm">DELETE</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      $('body').append(html);
      $modal = $('#customConfirmModal');
    }
    
    $modal.find('.custom-confirm-modal__message').text(message);
    $modal.fadeIn(200);
    
    $modal.find('.custom-confirm-modal__btn--cancel, .custom-confirm-modal__overlay').off('click').on('click', function() {
      $modal.fadeOut(200);
    });
    
    $modal.find('.custom-confirm-modal__btn--confirm').off('click').on('click', function() {
      $modal.fadeOut(200);
      onConfirm();
    });
  }

  // Delete Address Click Handler
  $(document).on('click', '.account-address-card__delete-btn', function (e) {
    e.preventDefault();
    e.stopPropagation();
    var targetUrl = $(this).attr('data-target');
    var confirmMsg = $(this).attr('data-confirm-message') || "Are you sure you want to delete this address?";
    
    showCustomConfirm(confirmMsg, function() {
      if (window.Shopify && window.Shopify.postLink) {
        window.Shopify.postLink(targetUrl, {
          parameters: { _method: 'delete' }
        });
      } else {
        var form = document.createElement('form');
        form.method = 'POST';
        form.action = targetUrl;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = '_method';
        input.value = 'delete';
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      }
    });
  });

  // Open Help Center Menu Popup
  $(document).on('click', '#OpenHelpCenterMenu', function (e) {
    e.preventDefault();
    $('#AccountMenuMobile').removeClass('is-open');
    $('#HelpCenterMenuMobile').addClass('is-open');
    $('html, body').addClass('account-scroll-lock overflow-hidden');
  });

  $(document).on('click', '#HelpCenterMenuMobile .account-menu-mobile__close, #HelpCenterMenuMobile .modal-overlay', function (e) {
    e.preventDefault();
    $('#HelpCenterMenuMobile').removeClass('is-open');
    setTimeout(function () {
      if ($('.account-overview-mobile-popup.is-open').length === 0 && !$('#AccountMenuMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  // Open Shipping Delivery Popup from Help Menu
  $(document).on('click', '#OpenShippingDeliveryFromHelp', function (e) {
    e.preventDefault();
    $('#HelpCenterMenuMobile').removeClass('is-open');
    $('#ShippingDeliveryMobile').addClass('is-open');
    $('html, body').addClass('account-scroll-lock overflow-hidden');
  });

  $(document).on('click', '#ShippingDeliveryMobile .account-overview-mobile__close, #ShippingDeliveryMobile .modal-overlay', function (e) {
    e.preventDefault();
    $('#ShippingDeliveryMobile').removeClass('is-open');
    setTimeout(function () {
      if ($('.account-overview-mobile-popup.is-open').length === 0 && !$('#AccountMenuMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  // Back from Shipping popup goes back to Help Center Menu
  $(document).on('click', '#BackToAccountMenuFromShipping', function (e) {
    e.preventDefault();
    $('#ShippingDeliveryMobile').removeClass('is-open');
    $('#HelpCenterMenuMobile').addClass('is-open');
  });

  // Back from Help Center Menu goes back to Account Menu
  $(document).on('click', '#BackToAccountMenuFromHelp', function (e) {
    e.preventDefault();
    $('#HelpCenterMenuMobile').removeClass('is-open');
    $('#AccountMenuMobile').addClass('is-open');
  });

  // Open Return Exchange Popup from Help Menu / Account Menu
  $(document).on('click', '#OpenReturnExchangeFromHelp', function (e) {
    e.preventDefault();
    $('#HelpCenterMenuMobile').removeClass('is-open');
    $('#ReturnExchangeMobile').addClass('is-open').data('referrer', 'help');
    $('html, body').addClass('account-scroll-lock overflow-hidden');
  });

  $(document).on('click', '#OpenReturnExchangeMobile', function (e) {
    e.preventDefault();
    $('#AccountMenuMobile').removeClass('is-open');
    $('#ReturnExchangeMobile').addClass('is-open').data('referrer', 'account');
    $('html, body').addClass('account-scroll-lock overflow-hidden');
  });

  $(document).on('click', '#ReturnExchangeMobile .account-overview-mobile__close, #ReturnExchangeMobile .modal-overlay', function (e) {
    e.preventDefault();
    $('#ReturnExchangeMobile').removeClass('is-open');
    setTimeout(function () {
      if ($('.account-overview-mobile-popup.is-open').length === 0 && !$('#AccountMenuMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  });

  $(document).on('click', '#BackToAccountMenuFromReturn', function (e) {
    e.preventDefault();
    var referrer = $('#ReturnExchangeMobile').data('referrer');
    $('#ReturnExchangeMobile').removeClass('is-open');
    if (referrer === 'help') {
      $('#HelpCenterMenuMobile').addClass('is-open');
    } else {
      $('#AccountMenuMobile').addClass('is-open');
    }
  });

  // Shipping Delivery Mobile Popup Accordion Toggle
  $(document).on('click', '#ShippingDeliveryMobile .accordion-header', function () {
    var $item = $(this).closest('.accordion-item');
    var isActive = $item.hasClass('is-active');
    $('#ShippingDeliveryMobile .accordion-item').removeClass('is-active');
    if (!isActive) {
      $item.addClass('is-active');
    }
  });

  // Initialize CountryProvinceSelector for mobile popups if available
  function initMobileCountryProvinceSelectors() {
    if (window.Shopify && window.Shopify.CountryProvinceSelector) {
      if (document.getElementById('AddressCountryMobile_New')) {
        new window.Shopify.CountryProvinceSelector('AddressCountryMobile_New', 'AddressProvinceMobile_New', {
          hideElement: 'AddressProvinceContainerMobile_New'
        });
      }
      $('[data-mobile-address-country-select]').each(function () {
        var formId = $(this).attr('data-form-id');
        new window.Shopify.CountryProvinceSelector('AddressCountryMobile_' + formId, 'AddressProvinceMobile_' + formId, {
          hideElement: 'AddressProvinceContainerMobile_' + formId
        });
      });
    }
  }

  // Run on load
  $(function() {
    initMobileCountryProvinceSelectors();
  });

  window.chHeaderModals.closeAccountSidebars = function () {
    $('.account-overview-mobile-popup, #AccountMenuMobile').removeClass('is-open');
    setTimeout(function () {
      if ($('.account-overview-mobile-popup.is-open').length === 0 && !$('#AccountMenuMobile').hasClass('is-open')) {
        $('html, body').removeClass('account-scroll-lock overflow-hidden');
      }
    }, 300);
  };
  */

  bindSearchEvents();
  bindAccountSidebarEvents();
  bindOverlayMaterialEvents();
  bindSupergaCareEvents();
  bindAccountOverviewForms();
  bindAccountAddressLayoutEvents();
  scheduleCollectionToolbarScrollInit();
  initInfiniteScroll();
  window.updateCollectionProductGridCount();
  scheduleAuthSidebarsOnLoad();
  bindSidebarMenuAccordion();

  // Custom Filter Drawer Functions
  window.openCustomFilterDrawer = function () {
    var drawer = document.getElementById('CustomFilterDrawer');
    if (drawer) {
      drawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
      window.syncCustomDrawerFromURL();
    }
  };

  window.closeCustomFilterDrawer = function () {
    var drawer = document.getElementById('CustomFilterDrawer');
    if (drawer) {
      drawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }
  };

  window.syncCustomDrawerFromURL = function () {
    var params = new URLSearchParams(window.location.search);
    var activeTags = params.getAll('filter.p.tag').map(function (t) { return decodeURIComponent(t).toLowerCase(); });
    var sortBy = params.get('sort_by') || 'manual';

    // Parse active tags from URL pathname (for handleized tag navigation, e.g. /collections/all/shoe-type-classic+size-35)
    var pathSegments = window.location.pathname.split('/');
    if (pathSegments.length > 2 && pathSegments[1] === 'collections') {
      var lastSegment = pathSegments[pathSegments.length - 1];
      if (lastSegment && !lastSegment.match(/^\d+$/)) {
        var pathTags = lastSegment.split('+').map(function (t) { return decodeURIComponent(t).toLowerCase(); });
        pathTags.forEach(function (pt) {
          if (!activeTags.includes(pt)) {
            activeTags.push(pt);
          }
        });
      }
    }

    // Sync Sort By
    var sortInputs = document.querySelectorAll('#CustomFilterForm input[name="sort_by"]');
    sortInputs.forEach(function (input) {
      input.checked = (input.value === sortBy);
    });

    // Sync Checkboxes
    var checkboxes = document.querySelectorAll('#CustomFilterForm input[type="checkbox"]');
    checkboxes.forEach(function (cb) {
      var valLower = decodeURIComponent(cb.value).toLowerCase();
      var valHandleized = valLower.replace(/[^a-z0-9]+/g, '-');
      cb.checked = activeTags.some(function (at) {
        return at === valLower || at === valHandleized || at.replace(/[^a-z0-9]+/g, '-') === valHandleized;
      });
    });
  };

  window.applyCustomFilters = function () {
    var form = document.getElementById('CustomFilterForm');
    if (!form) return;

    var formData = new FormData(form);
    var currentParams = new URLSearchParams(window.location.search);
    var nextParams = new URLSearchParams();

    // Preserve non-filter related search params
    currentParams.forEach(function (value, key) {
      if (key !== 'filter.p.tag' && key !== 'sort_by') {
        nextParams.set(key, value);
      }
    });

    // Add selected sort_by
    var selectedSort = formData.get('sort_by');
    if (selectedSort) {
      nextParams.set('sort_by', selectedSort);
    }

    // Add selected tags
    var selectedTags = formData.getAll('filter.p.tag');
    selectedTags.forEach(function (tag) {
      nextParams.append('filter.p.tag', tag);
    });

    var searchParams = nextParams.toString();

    // Close drawer first
    window.closeCustomFilterDrawer();

    // Trigger theme's AJAX re-render
    if (window.FacetFiltersForm && typeof window.FacetFiltersForm.renderPage === 'function') {
      window.FacetFiltersForm.updateCollectionToolbarState(searchParams);
      window.FacetFiltersForm.renderPage(searchParams, null);
    } else {
      window.location.search = searchParams;
    }
  };

  // Intercept the click of standard filter triggers to open our custom drawer instead
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-collection-filter-open]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      window.openCustomFilterDrawer();
    }
  });

  // ==========================================
  // LOGIKA NAVIGASI HALAMAN AKUN PADA MOBILE (LEFT-RIGHT PANEL TOGGLE)
  // ==========================================

  /**
   * Menginisialisasi logika show/hide panel detail di halaman Akun (Overview, Addresses, Purchases, dll.) pada mobile.
   * Logika ini membagi layout halaman menjadi panel menu kiri (sidebar) dan panel detail kanan (main content).
   */
  function initMobileAccountToggle() {
    var bodyContainer = document.querySelector('.customer-account-body');
    if (!bodyContainer) return;

    // Membaca parameter URL "open-detail" untuk mendeteksi apakah panel detail harus terbuka secara default
    var params = new URLSearchParams(window.location.search);
    var openDetail = params.get('open-detail');

    // Normalize path by removing locale prefixes (e.g. /id/account -> /account)
    var normalizedPath = window.location.pathname.replace(/^\/[a-z]{2}(-[a-zA-Z]{2})?(?=\/|$)/, '');
    var isMainPage = normalizedPath === '/account' || normalizedPath === '/account/' || normalizedPath === '/pages/shipping-delivery' || normalizedPath === '/pages/shipping-delivery/';
    var shouldOpenDetail = openDetail === 'true' || (!isMainPage && openDetail !== 'false');

    if (shouldOpenDetail) {
      bodyContainer.classList.add('active');
      document.body.classList.add('open-detail');
      document.body.classList.remove('show-menu');
    } else {
      bodyContainer.classList.remove('active');
      document.body.classList.remove('open-detail');
      document.body.classList.add('show-menu');
    }

    // Klik tombol Kembali (Back) di mobile detail panel (Menggunakan global native event delegation agar kebal dari update DOM dinamis & konflik jQuery di App Proxy)
    if (!window.accountBackBtnBound) {
      window.accountBackBtnBound = true;
      document.addEventListener('click', function (e) {
        var backBtn = e.target.closest('.account-title-close');
        if (backBtn) {
          e.preventDefault();
          var bodyContainer = document.querySelector('.customer-account-body');
          if (!bodyContainer) return;

          // Hapus parameter open-detail dari URL tanpa reload halaman (pushState)
          var currentParams = new URLSearchParams(window.location.search);
          currentParams.delete('open-detail');
          var queryString = currentParams.toString();
          var newUrl = window.location.pathname + (queryString ? '?' + queryString : '') + window.location.hash;
          window.history.pushState({}, '', newUrl);

          // Sembunyikan panel detail kanan, kembali tampilkan menu kiri
          bodyContainer.classList.remove('active');
          document.body.classList.remove('open-detail');
          document.body.classList.add('show-menu');
        }
      });
    }

    // Mengelola klik tautan menu samping (sidebar menu) di mobile
    var menuLinks = document.querySelectorAll('.customer-sidebar-item a');
    menuLinks.forEach(function (link) {
      if (link.dataset.bound) return;
      link.dataset.bound = 'true';

      link.addEventListener('click', function (e) {
        var href = this.getAttribute('href');
        if (!href || href === '#' || href.startsWith('javascript:')) return;

        // Abaikan link Sign Out agar proses logout berjalan normal
        if (href.indexOf('logout') !== -1) return;

        var targetUrl = new URL(href, window.location.origin);

        var currentPathNormalized = window.location.pathname.replace(/^\/[a-z]{2}(-[a-zA-Z]{2})?(?=\/|$)/, '');
        var targetPathNormalized = targetUrl.pathname.replace(/^\/[a-z]{2}(-[a-zA-Z]{2})?(?=\/|$)/, '');
        var isCurrentPage = targetPathNormalized === currentPathNormalized;

        if (isCurrentPage) {
          // Jika link yang diklik adalah halaman aktif saat ini, ubah parameter URL dan buka panel kanan tanpa reload
          e.preventDefault();
          var currentParams = new URLSearchParams(window.location.search);
          currentParams.set('open-detail', 'true');
          var newUrl = window.location.pathname + '?' + currentParams.toString() + window.location.hash;
          window.history.pushState({}, '', newUrl);
          bodyContainer.classList.add('active');
          document.body.classList.add('open-detail');
          document.body.classList.remove('show-menu');
        } else {
          // Jika berpindah ke halaman akun lain (misal dari /account ke /account/addresses), 
          // paksakan reload halaman tujuan dengan menyertakan ?open-detail=true agar panel detailnya langsung terbuka.
          // Kecuali jika berpindah antar-section (seperti dari Help Center ke Account/My Account).
          e.preventDefault();
          var targetParams = new URLSearchParams(targetUrl.search);
          var targetPathNormalized = targetUrl.pathname.replace(/^\/[a-z]{2}(-[a-zA-Z]{2})?(?=\/|$)/, '');

          var isCurrentAccount = currentPathNormalized.indexOf('/account') === 0 || currentPathNormalized.indexOf('/pages/your-purchase') === 0 || currentPathNormalized.indexOf('/a/wishlist') === 0;
          var isTargetAccount = targetPathNormalized.indexOf('/account') === 0 || targetPathNormalized.indexOf('/pages/your-purchase') === 0 || targetPathNormalized.indexOf('/a/wishlist') === 0;

          var isCurrentHelp = currentPathNormalized.indexOf('/pages/shipping-delivery') === 0 || currentPathNormalized.indexOf('/pages/return-exchange') === 0;
          var isTargetHelp = targetPathNormalized.indexOf('/pages/shipping-delivery') === 0 || targetPathNormalized.indexOf('/pages/return-exchange') === 0;

          var isInternalNavigation = (isCurrentAccount && isTargetAccount) || (isCurrentHelp && isTargetHelp);

          if (isInternalNavigation) {
            targetParams.set('open-detail', 'true');
          } else {
            targetParams.delete('open-detail');
          }

          var targetQuery = targetParams.toString();
          window.location.href = targetUrl.pathname + (targetQuery ? '?' + targetQuery : '') + targetUrl.hash;
        }
      });
    });
  }

  // Mendengarkan tombol Back/Forward browser (popstate) untuk menyesuaikan panel aktif
  window.addEventListener('popstate', function () {
    var bodyContainer = document.querySelector('.customer-account-body');
    if (!bodyContainer) return;
    var currentParams = new URLSearchParams(window.location.search);
    var openDetail = currentParams.get('open-detail');
    var normalizedPath = window.location.pathname.replace(/^\/[a-z]{2}(-[a-zA-Z]{2})?(?=\/|$)/, '');
    var isMainPage = normalizedPath === '/account' || normalizedPath === '/account/' || normalizedPath === '/pages/shipping-delivery' || normalizedPath === '/pages/shipping-delivery/';
    var shouldOpenDetail = openDetail === 'true' || (!isMainPage && openDetail !== 'false');

    if (shouldOpenDetail) {
      bodyContainer.classList.add('active');
      document.body.classList.add('open-detail');
      document.body.classList.remove('show-menu');
    } else {
      bodyContainer.classList.remove('active');
      document.body.classList.remove('open-detail');
      document.body.classList.add('show-menu');
    }
  });

  // Merekam URL halaman asal ketika mengklik tombol Akun di sidebar menu mobile
  $(document).on('click', '.custom-sidebar-menu__inner-top-actions a, a.header__icon--account', function (e) {
    var href = this.getAttribute('href');
    if (href && href.indexOf('logout') === -1 && href.indexOf('login') === -1) {
      // Simpan URL halaman terakhir sebelum dialihkan ke halaman Akun
      localStorage.setItem('account_return_url', window.location.href);
    }
  });

  // Klik tombol Close (X) di header menu akun mobile untuk mengembalikan ke halaman asal yang tersimpan
  $(document).on('click', '.account-menu-mobile__close', function (e) {
    e.preventDefault();
    var returnUrl = localStorage.getItem('account_return_url');
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      window.location.href = '/'; // Fallback jika URL asal tidak ditemukan
    }
  });

  // Reusable custom confirm modal function
  function showCustomConfirm(message, onConfirm) {
    var $modal = $('#customConfirmModal');
    if (!$modal.length) {
      var html =
        '<div class="custom-confirm-modal" id="customConfirmModal" style="display:none;">' +
        '<div class="custom-confirm-modal__overlay"></div>' +
        '<div class="custom-confirm-modal__content">' +
        '<p class="custom-confirm-modal__message"></p>' +
        '<div class="custom-confirm-modal__buttons">' +
        '<button type="button" class="custom-confirm-modal__btn custom-confirm-modal__btn--cancel">CANCEL</button>' +
        '<button type="button" class="custom-confirm-modal__btn custom-confirm-modal__btn--confirm">DELETE</button>' +
        '</div>' +
        '</div>' +
        '</div>';
      $('body').append(html);
      $modal = $('#customConfirmModal');
    }

    $modal.find('.custom-confirm-modal__message').text(message);
    $modal.fadeIn(200);

    $modal.find('.custom-confirm-modal__btn--cancel, .custom-confirm-modal__overlay').off('click').on('click', function () {
      $modal.fadeOut(200);
    });

    $modal.find('.custom-confirm-modal__btn--confirm').off('click').on('click', function () {
      $modal.fadeOut(200);
      onConfirm();
    });
  }

  // Delete Address Click Handler
  $(document).on('click', '.account-address-card__delete-btn', function (e) {
    e.preventDefault();
    e.stopPropagation();
    var targetUrl = $(this).attr('data-target');
    var confirmMsg = $(this).attr('data-confirm-message') || "Are you sure you want to delete this address?";

    showCustomConfirm(confirmMsg, function () {
      if (window.Shopify && window.Shopify.postLink) {
        window.Shopify.postLink(targetUrl, {
          parameters: { _method: 'delete' }
        });
      } else {
        var form = document.createElement('form');
        form.method = 'POST';
        form.action = targetUrl;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = '_method';
        input.value = 'delete';
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      }
    });
  });

  // Sidebar Menu Clicks (Clicking anywhere in customer-sidebar-item triggers the child link click natively)
  $(document).on('click', '.customer-sidebar-item', function (e) {
    if (!$(e.target).closest('a').length) {
      var link = this.querySelector('a');
      if (link) {
        link.click();
      }
    }
  });

  // Jalankan inisialisasi awal
  initMobileAccountToggle();
  document.addEventListener('shopify:section:load', initMobileAccountToggle);
});

